import React, { useState, useRef, useEffect, createContext, useContext } from "react";
import {
  X, Search, Send, Check, CheckCheck, Paperclip, Mic, Square, FileText, Download, MessageSquareReply,
  Maximize2, Minimize2, Plus, Users, Hash, Video, UserPlus, Calendar, ChevronDown, ChevronUp,
  PhoneOff, VolumeX, Volume2, Copy, Forward, Reply, MoreHorizontal, Camera, Building2, Lock,
  Settings, Trash2, Pencil, ShieldCheck, UserMinus, Pin, PinOff,
} from "lucide-react";
import { K } from "../theme";
import { Av, Inp } from "../components";
import { fmtTime } from "../utils/timezone";
import { notify, confirmDialog } from "../notify";
import api from "../api";
import { uploadUrl } from "../uploadUrl";
import { fmtDDMMYY } from "../dateFormat";
import { QUICK_REACTIONS, EMOJI_CATEGORIES } from "./emoji";

const attachmentUrl = (m) => m.attachment_path ? uploadUrl(m.attachment_path) : m._previewUrl;

const fmtBytes = (n) => {
  if (!n && n !== 0) return '';
  if (n < 1024) return n + ' B';
  if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB';
  return (n / (1024 * 1024)).toFixed(1) + ' MB';
};

const firstName = (name) => (name || '').trim().split(/\s+/)[0] || 'there';

// Lively, continuously-animated greeting for the empty-thread state — a
// waving hand (looping rotate) under a bouncing "Hi!" (each letter bounces
// on its own staggered delay, never settling). Built from emoji + CSS only
// — no sticker/GIF asset to source or license, matching the same
// always-moving feel as a Telegram-style sticker.
const ChatMascot = () => (
  <div className="flex flex-col items-center">
    <div className="flex gap-[1px]" aria-hidden="true">
      {['H', 'i', '!'].map((ch, i) => (
        <span
          key={i}
          className="text-[26px] font-extrabold animate-mascotLetterBounce"
          style={{ color: [K.p, K.g, K.a][i], animationDelay: `${i * 0.12}s` }}
        >{ch}</span>
      ))}
    </div>
    <div className="text-[48px] leading-none animate-mascotWave" style={{ transformOrigin: '72% 72%' }}>👋</div>
  </div>
);

// Shown instead of a blank scroll area when a thread has no messages yet.
const EmptyThread = ({ person }) => (
  <div className="flex-1 flex flex-col items-center justify-center text-center px-6 gap-3">
    <ChatMascot />
    <div>
      <div className="text-[16px] font-extrabold text-t0">Say hi to {firstName(person?.name)}!</div>
      <div className="text-[13px] text-t3 mt-[4px] max-w-[230px] leading-[1.4]">Send your first message below.</div>
    </div>
  </div>
);

const TypingDots = () => (
  <div className="flex items-center gap-[3px] px-3 py-[9px] bg-bg3 rounded-2xl rounded-bl-[4px] w-fit">
    {[0, 1, 2].map(i => (
      <span key={i} className="w-[6px] h-[6px] rounded-full bg-t3 animate-chatTyping" style={{ animationDelay: `${i * 0.15}s` }} />
    ))}
  </div>
);

// Only "mine" messages show a tick. Plain-text bubbles sit on solid blue
// (bg-p), so need the washed-out white/sky-blue set; attachment cards are
// a neutral light card regardless of sender (see Attachment below), so they
// read fine with ordinary muted-gray ticks — pass `light` for those.
const Tick = ({ msg, myUserId, light }) => {
  if (msg.sender_id !== myUserId) return null;
  if (msg.read_at) return <CheckCheck size={13} color={light ? K.p : "#BAE6FD"} />;
  if (msg.delivered_at) return <CheckCheck size={13} color={light ? K.t3 : "rgba(255,255,255,.8)"} />;
  return <Check size={13} color={light ? K.t3 : "rgba(255,255,255,.8)"} />;
};

// Group ticks: one grey = sent, two grey = read by some, two blue = read by all others.
const GroupTick = ({ m, light }) => {
  const read = m.read_by_count || 0;
  const all = m.member_count > 0 && read >= m.member_count;
  if (all) return <CheckCheck size={13} color={light ? K.p : "#BAE6FD"} />;
  if (read > 0) return <CheckCheck size={13} color={light ? K.t3 : "rgba(255,255,255,.8)"} />;
  return <Check size={13} color={light ? K.t3 : "rgba(255,255,255,.8)"} />;
};

// Day-relative label like most chat apps ("just now" / "12:04" is enough for
// this in-app widget — full date isn't needed for a same-day-mostly tool).
const msgTime = (t) => fmtTime(t);

const fileExt = (name) => (name || '').split('.').pop()?.toLowerCase().slice(0, 4) || 'file';
// Loosely mirrors common office-suite icon colors so a glance tells you the
// file kind, the way WhatsApp/Drive file chips do.
const EXT_COLORS = {
  xls: '#16A34A', xlsx: '#16A34A', csv: '#16A34A',
  pdf: '#DC2626', doc: '#2563EB', docx: '#2563EB',
  ppt: '#EA580C', pptx: '#EA580C', zip: '#78716C', txt: K.t3,
};

// Media/file attachments render as a neutral light card, never the
// sender-colored (blue) text bubble — a solid blue image/audio/file card
// read as "too much blue" in review. Only plain text keeps the blue fill;
// see the bubble className logic in ThreadView below.
// WhatsApp-style ring over an attachment still mid-upload: fills clockwise
// as the real byte progress comes in from XHR's upload.onprogress, falling
// back to a slow indeterminate creep if the browser never reports a
// length (so it never just sits frozen at 0% on a slow connection).
const UploadProgressRing = ({ progress }) => {
  const [fallback, setFallback] = useState(0.04);
  useEffect(() => {
    if (progress > 0) return;
    const id = setInterval(() => setFallback(p => (p < 0.82 ? p + 0.02 : p)), 200);
    return () => clearInterval(id);
  }, [progress]);
  const pct = Math.max(0.03, Math.min(1, progress || fallback));
  const r = 15, c = 2 * Math.PI * r;
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-[10px]">
      <svg width="38" height="38" viewBox="0 0 38 38" className="-rotate-90">
        <circle cx="19" cy="19" r={r} fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="3" />
        <circle
          cx="19" cy="19" r={r} fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c * (1 - pct)}
          style={{ transition: 'stroke-dashoffset 0.15s linear' }}
        />
      </svg>
    </div>
  );
};

const Attachment = ({ m }) => {
  const url = attachmentUrl(m);
  if (!url) return null;
  const uploading = m._uploading && !m._failed;
  if (m.attachment_kind === 'image') {
    return (
      <div className="relative inline-block">
        <img
          src={url}
          alt={m.attachment_name || 'image'}
          onClick={() => !uploading && window.open(url, '_blank')}
          className="max-w-[220px] max-h-[220px] rounded-[10px] object-cover cursor-pointer block"
        />
        {uploading && <UploadProgressRing progress={m._uploadProgress} />}
      </div>
    );
  }
  if (m.attachment_kind === 'audio') {
    return (
      <div className="relative p-2 rounded-[10px] bg-bg2 border border-b0 min-w-[230px]">
        <audio controls src={url} className="w-full h-[34px]" />
        {uploading && <UploadProgressRing progress={m._uploadProgress} />}
      </div>
    );
  }
  if (m.attachment_kind === 'video') {
    return (
      <div className="relative inline-block">
        <video controls src={url} className="max-w-[260px] max-h-[260px] rounded-[10px] bg-black block" />
        {uploading && <UploadProgressRing progress={m._uploadProgress} />}
      </div>
    );
  }
  const ext = fileExt(m.attachment_name);
  return (
    <a
      href={uploading ? undefined : url}
      target="_blank"
      rel="noreferrer"
      download={m.attachment_name}
      onClick={e => uploading && e.preventDefault()}
      className="relative flex items-center gap-[10px] px-[10px] py-[9px] rounded-[10px] no-underline min-w-[200px] bg-bg2 border border-b0 hover:bg-bg3 transition-colors"
    >
      <div className="w-[36px] h-[36px] rounded-[8px] flex items-center justify-center flex-shrink-0" style={{ background: EXT_COLORS[ext] || K.t3 }}>
        <FileText size={18} color="#fff" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-semibold text-t0 truncate">{m.attachment_name || 'File'}</div>
        <div className="text-[11px] text-t3 uppercase">{ext} · {fmtBytes(m.attachment_size)}</div>
      </div>
      <Download size={16} className="flex-shrink-0 text-t3" />
      {uploading && <UploadProgressRing progress={m._uploadProgress} />}
    </a>
  );
};

const GroupRow = ({ g, active, onClick, onTogglePin }) => (
  <div
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={e => { if (e.key === 'Enter') onClick(); }}
    className={`group w-full flex items-center gap-[10px] px-3 py-[9px] rounded-xl text-left cursor-pointer transition-colors ${active ? 'bg-pg' : 'bg-transparent hover:bg-bg3'}`}
  >
    {g.avatar_path ? (
      <img src={uploadUrl(g.avatar_path)} alt="" className="w-[38px] h-[38px] rounded-full object-cover flex-shrink-0" />
    ) : (
      <div className="w-[38px] h-[38px] rounded-full flex items-center justify-center flex-shrink-0 text-white" style={{ background: g.kind === 'channel' ? K.tl : K.pu }}>
        {g.kind === 'channel' ? <Hash size={17} /> : <Users size={17} />}
      </div>
    )}
    <div className="min-w-0 flex-1">
      <div className="text-[14px] font-bold text-t0 truncate flex items-center gap-1">
        {g.pinned && <Pin size={11} className="text-p flex-shrink-0 fill-current" />}
        <span className="truncate">{g.name}</span>
      </div>
      <div className="text-[12px] text-t3 truncate">
        {g.last_message ? `${g.last_sender_name ? g.last_sender_name.split(' ')[0] + ': ' : ''}${g.last_message}` : `${g.member_count} member${g.member_count === 1 ? '' : 's'}`}
      </div>
    </div>
    {onTogglePin && (
      <button
        onClick={e => { e.stopPropagation(); onTogglePin(g); }}
        className={`w-[22px] h-[22px] rounded-full bg-transparent border-0 cursor-pointer flex items-center justify-center flex-shrink-0 transition-opacity ${g.pinned ? 'text-p opacity-100' : 'text-t3 opacity-0 group-hover:opacity-100 hover:bg-bg2'}`}
        title={g.pinned ? 'Unpin' : 'Pin to top'}
      >
        {g.pinned ? <PinOff size={13} /> : <Pin size={13} />}
      </button>
    )}
    {g.unread_count > 0 && (
      <span className="min-w-[19px] h-[19px] px-[5px] rounded-full bg-r text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0">{g.unread_count}</span>
    )}
  </div>
);

// Renders a message body with (1) any http(s) URL turned into a real
// clickable link and (2) "@Name" occurrences that match an actually-
// mentioned member bolded — matching only real mentions (not any "@" the
// sender happened to type), never both lost by handling just one and
// stringifying the rest. URLs are matched first at the top level so a
// mention can never land inside a link's own text.
const URL_SPLIT_RE = /(https?:\/\/[^\s]+)/g;
const isUrl = (s) => /^https?:\/\//.test(s);
// Local calendar-day key for grouping messages into date sections.
const dayKeyOf = (t) => {
  const d = new Date(t);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const dayLabelOf = (t) => {
  const key = dayKeyOf(t);
  const today = dayKeyOf(new Date());
  const yest = dayKeyOf(new Date(Date.now() - 86400000));
  if (key === today) return 'Today';
  if (key === yest) return 'Yesterday';
  return fmtDDMMYY(key);
};
// Small grey pill shown above the first message of each day, WhatsApp-style.
const DaySeparator = ({ ts }) => (
  <div className="flex justify-center my-[10px]">
    <span className="px-[10px] py-[2px] rounded-[8px] bg-bg3 text-t3 text-[11px] font-semibold">{dayLabelOf(ts)}</span>
  </div>
);
// Centred grey line for membership events ("Shubham added Komal").
const SystemLine = ({ m }) => (
  <div className="flex justify-center my-[6px]">
    <span className="px-[10px] py-[3px] rounded-[8px] bg-bg3 text-t2 text-[11.5px] text-center max-w-[80%]">{m.body}</span>
  </div>
);

const renderMessageBody = (body, { mentionedUserIds, members, mine = false } = {}) => {
  if (!body) return body;
  const mentionedNames = (mentionedUserIds || [])
    .map(uid => members?.find(m => m.user_id === uid)?.name)
    .filter(Boolean)
    .sort((a, b) => b.length - a.length); // longest first so a prefix name doesn't shadow a longer one
  const mentionRe = mentionedNames.length
    ? new RegExp(`(@(?:${mentionedNames.map(n => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')}))`, 'g')
    : null;

  return body.split(URL_SPLIT_RE).map((part, i) => {
    if (isUrl(part)) {
      return <a key={i} href={part} target="_blank" rel="noreferrer" className="underline break-all" style={{ color: 'inherit' }}>{part}</a>;
    }
    if (!mentionRe) return <React.Fragment key={i}>{part}</React.Fragment>;
    return (
      <React.Fragment key={i}>
        {part.split(mentionRe).map((seg, j) => mentionedNames.some(n => seg === '@' + n)
          ? <strong key={j} className="font-bold" style={mine ? { color: "#fff", textDecoration: "underline" } : { color: K.p }}>{seg}</strong>
          : <React.Fragment key={j}>{seg}</React.Fragment>)}
      </React.Fragment>
    );
  });
};

// What actually gets copied/forwarded for a message — its text, or for an
// attachment-only message, a link to the file (forwarding doesn't re-upload
// the file itself, just shares the same URL, which already works since
// /uploads is public).
const forwardableText = (m) => m.body || (m.attachment_path ? uploadUrl(m.attachment_path) : '');

// The four quick-react emojis offered on every message — thumbs up plus
// the three next most commonly used reactions (heart, laugh, surprised).
// Tapping an already-active one toggles it back off (handled server-side).
// Shown when the "+" button is clicked in a message's quick-react row —
// replaces the row with a taller, scrollable, category-labeled grid (same
// layout convention as WhatsApp/standard emoji keyboards: faces first,
// then gestures, hearts, animals, food, and so on in order).
const EmojiPicker = ({ onPick }) => (
  <div className="max-h-[260px] w-[260px] overflow-y-auto p-[6px]">
    {EMOJI_CATEGORIES.map(cat => (
      <div key={cat.label} className="mb-[4px]">
        <div className="text-[9.5px] font-bold uppercase tracking-wide text-t3 px-[4px] pt-[4px] pb-[2px] sticky top-0 bg-bg1">{cat.label}</div>
        <div className="grid grid-cols-8 gap-[1px]">
          {cat.emojis.map(emoji => (
            <button
              key={emoji}
              onClick={() => onPick(emoji)}
              className="text-[17px] bg-transparent border-0 cursor-pointer p-[3px] rounded-[6px] hover:bg-bg3 leading-none"
              title={`React with ${emoji}`}
            >
              {emoji}
            </button>
          ))}
        </div>
      </div>
    ))}
  </div>
);

// Reaction pill bar rendered under a message's bubble when it has at least
// one reaction — each pill is itself a toggle for the current user.
const ReactionBar = ({ m, onReact, mine }) => {
  const [pop, setPop] = useState(null); // { emoji, top, left, rows }
  if (!m.reactions?.length) return null;
  const openWho = (e, emoji) => {
    const r = e.currentTarget.getBoundingClientRect();
    const width = 220;
    const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
    const top = r.bottom + 4 + 240 > window.innerHeight ? Math.max(8, r.top - 244) : r.bottom + 4;
    setPop({ emoji, top, left, rows: null });
    api.get(`/api/chat/messages/${m.id}/reactions`)
      .then(d => setPop(p => (p ? { ...p, rows: d?.data || [] } : p)))
      .catch(() => setPop(p => (p ? { ...p, rows: [] } : p)));
  };
  const rows = pop?.rows ? [...pop.rows].sort((a, b) => (b.emoji === pop.emoji) - (a.emoji === pop.emoji)) : null;
  return (
    <div className={`flex flex-wrap gap-[4px] mt-[3px] ${mine ? 'justify-end' : 'justify-start'}`}>
      {m.reactions.map(r => (
        <button
          key={r.emoji}
          onClick={(e) => openWho(e, r.emoji)}
          title="See who reacted"
          className={`flex items-center gap-[3px] px-[7px] py-[1px] rounded-full border text-[11px] cursor-pointer ${r.reacted_by_me ? 'bg-pg border-p text-p' : 'bg-bg1 border-b0 text-t2 hover:bg-bg3'}`}
        >
          <span>{r.emoji}</span>
          {r.count > 1 && <span className="font-semibold">{r.count}</span>}
        </button>
      ))}
      {pop && (
        <>
          <div className="fixed inset-0 z-[590]" onClick={() => setPop(null)} />
          <div style={{ position: 'fixed', top: pop.top, left: pop.left, width: 220 }} className="z-[600] bg-bg1 border border-b0 rounded-[8px] shadow-[0_8px_24px_rgba(15,23,42,0.18)] py-1 max-h-[240px] overflow-y-auto">
            <div className="px-3 py-[5px] text-[11px] font-bold text-t3 border-b border-b0">Reactions</div>
            {!rows && <div className="px-3 py-2 text-[12px] text-t3">Loading…</div>}
            {rows && rows.length === 0 && <div className="px-3 py-2 text-[12px] text-t3">No reactions</div>}
            {rows && rows.map((x, i) => (
              <div key={x.user_id + x.emoji + i} className="flex items-center gap-2 px-3 py-[6px] text-[12px] text-t0">
                <span className="text-[15px] leading-none">{x.emoji}</span>
                <span className="flex-1 truncate font-semibold">{x.is_me ? 'You' : x.name}</span>
                {x.is_me && (
                  <button onClick={() => { setPop(null); onReact(m.id, x.emoji); }} className="text-[11px] font-semibold text-r bg-transparent border-0 cursor-pointer hover:underline">Remove</button>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Placeholder bubble for a message its sender deleted.
const DeletedLine = ({ m, mine, isNewRun }) => (
  <div className={`flex ${mine ? 'justify-end' : 'justify-start'} ${isNewRun ? 'mt-[8px]' : ''}`}>
    <div className="rounded-2xl px-3 py-[7px] text-[12px] italic text-t3 bg-bg1 border border-b0 flex items-center gap-[6px]">
      <span>🚫 This message was deleted</span>
      <span className="text-[10px] not-italic">{msgTime(m.created_at)}</span>
    </div>
  </div>
);

// Collapsed bar at the top of a thread showing how many messages are
// pinned (with a preview of the latest one); expands into the full list on
// click, each row offering Unpin. Pins are shared with the whole
// conversation, so this is the same for everyone who opens the thread, not
// a personal view.
// Shared by both the composer's "replying to" bar and the quoted block
// rendered inside a sent reply's own bubble.
const quotePreviewText = (m) => m?.reply_to_body ?? m?.body ?? (
  (m?.reply_to_attachment_kind || m?.attachment_kind) ? `📎 ${m?.reply_to_attachment_name || m?.attachment_name || m?.reply_to_attachment_kind || m?.attachment_kind}` : ''
);

// WhatsApp-style "replying to ..." strip shown above the composer once
// Reply is picked on a message — cleared on send or via its own X.
const ReplyPreviewBar = ({ replyingTo, onCancel }) => {
  if (!replyingTo) return null;
  return (
    <div className="flex items-center gap-2 px-3 py-[6px] bg-bg2 border-t border-b0 flex-shrink-0">
      <div className="w-[3px] self-stretch rounded-full flex-shrink-0" style={{ background: K.p }} />
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-bold" style={{ color: K.p }}>{replyingTo.sender_name}</div>
        <div className="text-[11.5px] text-t2 truncate">{quotePreviewText(replyingTo)}</div>
      </div>
      <button onClick={onCancel} className="w-[20px] h-[20px] rounded-full bg-transparent text-t3 hover:bg-bg3 flex items-center justify-center border-0 cursor-pointer flex-shrink-0">
        <X size={13} />
      </button>
    </div>
  );
};

// Small quoted block inside a message that was sent as a reply — click to
// scroll to (and briefly highlight) the original in this same chat, the
// way WhatsApp's quote block works. Silently renders nothing if the
// original has scrolled out of the currently loaded page of messages.
const QuotedMessageBlock = ({ m, mine, onClick }) => {
  if (!m.reply_to_message_id) return null;
  return (
    <button
      onClick={() => onClick(m.reply_to_message_id)}
      className={`block w-full text-left mb-[4px] px-[8px] py-[5px] rounded-[6px] border-l-[3px] cursor-pointer border-0 ${mine ? 'bg-white/15' : 'bg-bg2'}`}
      style={{ borderLeftWidth: 3, borderLeftStyle: 'solid', borderLeftColor: mine ? 'rgba(255,255,255,.6)' : K.p }}
    >
      <div className={`text-[10.5px] font-bold truncate ${mine ? 'text-white/90' : ''}`} style={mine ? {} : { color: K.p }}>{m.reply_to_sender_name}</div>
      <div className={`text-[11px] truncate ${mine ? 'text-white/70' : 'text-t3'}`}>{quotePreviewText(m)}</div>
    </button>
  );
};

const PinnedMessagesBar = ({ chat }) => {
  const [open, setOpen] = useState(false);
  if (!chat.pinnedMessages.length) return null;
  const latest = chat.pinnedMessages[0];
  const preview = (m) => m.body || (m.attachment_kind ? `📎 ${m.attachment_name || m.attachment_kind}` : '');
  return (
    <div className="relative border-b border-b0 flex-shrink-0">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center gap-2 px-3 py-[7px] bg-pg hover:bg-bg3 border-0 cursor-pointer text-left transition-colors">
        <Pin size={13} className="text-p flex-shrink-0 fill-current" />
        <div className="min-w-0 flex-1">
          <div className="text-[10.5px] font-bold text-p">{chat.pinnedMessages.length} pinned message{chat.pinnedMessages.length === 1 ? '' : 's'}</div>
          <div className="text-[11px] text-t2 truncate">{preview(latest)}</div>
        </div>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute left-0 right-0 top-full z-30 max-h-[240px] overflow-y-auto bg-bg1 border border-b0 border-t-0 shadow-[0_8px_24px_rgba(15,23,42,0.18)]">
            {chat.pinnedMessages.map(m => (
              <div key={m.id} className="flex items-start gap-2 px-3 py-[8px] border-b border-b0 last:border-0">
                <div className="min-w-0 flex-1">
                  <div className="text-[11.5px] font-semibold text-t0 truncate">{m.sender_name}</div>
                  <div className="text-[12px] text-t2 truncate">{preview(m)}</div>
                  <div className="text-[10px] text-t3">Pinned by {m.pinned_by_name}</div>
                </div>
                <button
                  onClick={() => chat.togglePinMessage(m.id, true)}
                  className="w-[22px] h-[22px] rounded-full bg-transparent text-t3 hover:text-r hover:bg-bg2 flex items-center justify-center border-0 cursor-pointer flex-shrink-0"
                  title="Unpin"
                >
                  <PinOff size={13} />
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Small hover-reveal "..." button on every message bubble, opening a quick
// emoji-react row plus a Copy / Forward menu. `className` lets the two
// callers (DM vs group thread) nudge its position since bubbles sit on
// opposite sides there.
const MessageActionsMenu = ({ m, onForward, onOpenThread, onReact, onTogglePin, onReply, onDelete, onEdit, className = '' }) => {
  const [editing, setEditing] = useState(null); // draft text while the edit box is open
  const [editErr, setEditErr] = useState('');
  const [open, setOpen] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const text = forwardableText(m);

  const copy = () => {
    if (text) navigator.clipboard?.writeText(text).catch(() => {});
    setOpen(false);
  };

  const react = (emoji) => { setOpen(false); setShowPicker(false); onReact(m.id, emoji); };

  // `fixed` + a position computed from the button's own screen location,
  // instead of `absolute right-0` — the chat panel itself clips overflow
  // (rounded-corner card), and for a "mine" bubble pushed hard right the
  // old absolute dropdown often had nowhere near enough room to its left
  // before hitting that edge, silently cutting its left side off. Clamping
  // against window width/height here sidesteps that regardless of which
  // side the bubble sits on or how narrow the panel is. Recomputed with a
  // wider box when the full emoji picker opens, since it's wider/taller
  // than the plain quick-react + menu list.
  const computePos = (width, height) => {
    const r = btnRef.current.getBoundingClientRect();
    let left = r.right - width;
    left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
    let top = r.bottom + 4;
    if (top + height > window.innerHeight) top = Math.max(8, r.top - height - 4); // flip above if no room below
    return { top, left };
  };

  const toggle = () => {
    if (!open && btnRef.current) {
      setPos(computePos(180, 220));
    } else {
      setShowPicker(false);
    }
    setOpen(o => !o);
  };

  const openPicker = () => {
    if (btnRef.current) setPos(computePos(272, 300));
    setShowPicker(true);
  };

  if (!text) return null; // nothing to copy/forward for a still-uploading or bodyless row
  return (
    <div className={`relative ${className}`}>
      {editing !== null && (
        <div className="fixed inset-0 z-[700] bg-black/40 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="w-full max-w-[420px] bg-bg1 rounded-[10px] shadow-xl p-4" onClick={e => e.stopPropagation()}>
            <div className="text-[13px] font-bold text-t0 mb-2">Edit message</div>
            <textarea
              autoFocus value={editing} onChange={e => setEditing(e.target.value)} rows={4}
              className="w-full text-[13px] text-t0 bg-bg2 border border-b0 rounded-[8px] p-2 outline-none resize-none"
            />
            {editErr && <div className="text-[12px] text-r mt-1">{editErr}</div>}
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => setEditing(null)} className="px-3 py-[6px] text-[12px] font-semibold rounded-[8px] bg-bg3 text-t0 border-0 cursor-pointer">Cancel</button>
              <button
                disabled={!editing.trim() || editing.trim() === m.body}
                onClick={async () => { const err = await onEdit(m.id, editing.trim()); if (err) setEditErr(err); else setEditing(null); }}
                className="px-3 py-[6px] text-[12px] font-semibold rounded-[8px] bg-p text-white border-0 cursor-pointer disabled:opacity-50"
              >Save</button>
            </div>
          </div>
        </div>
      )}
      <button
        ref={btnRef}
        onClick={toggle}
        className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity w-[22px] h-[22px] rounded-full bg-bg1 border border-b0 text-t3 flex items-center justify-center cursor-pointer shadow-sm"
        title="More"
      >
        <MoreHorizontal size={13} />
      </button>
      {open && pos && (
        <>
          <div className="fixed inset-0 z-[590]" onClick={() => setOpen(false)} />
          <div
            style={{ position: 'fixed', top: pos.top, left: pos.left }}
            className={`z-[600] ${showPicker ? 'w-[272px]' : 'w-[180px]'} bg-bg1 border border-b0 rounded-[8px] shadow-[0_8px_24px_rgba(15,23,42,0.18)] py-1`}
          >
            {onReact && !m._optimistic && showPicker && (
              <div className="border-b border-b0">
                <EmojiPicker onPick={react} />
              </div>
            )}
            {onReact && !m._optimistic && !showPicker && (
              <div className="flex items-center justify-between px-[8px] py-[6px] border-b border-b0">
                {QUICK_REACTIONS.map(emoji => (
                  <button
                    key={emoji}
                    onClick={() => react(emoji)}
                    className="text-[16px] bg-transparent border-0 cursor-pointer p-[2px] rounded-[6px] hover:bg-bg3 leading-none"
                    title={`React with ${emoji}`}
                  >
                    {emoji}
                  </button>
                ))}
                <button
                  onClick={openPicker}
                  className="w-[20px] h-[20px] rounded-[6px] bg-bg2 text-t3 flex items-center justify-center border-0 cursor-pointer hover:bg-bg3 flex-shrink-0"
                  title="More emoji"
                >
                  <Plus size={12} />
                </button>
              </div>
            )}
            {onReply && !m._optimistic && (
              <button onClick={() => { setOpen(false); onReply(m); }} className="w-full flex items-center gap-[6px] text-left px-3 py-[7px] text-[12px] font-semibold text-t0 bg-transparent border-0 cursor-pointer hover:bg-bg3">
                <Reply size={13} className="text-t3" /> Reply
              </button>
            )}
            <button onClick={copy} className="w-full flex items-center gap-[6px] text-left px-3 py-[7px] text-[12px] font-semibold text-t0 bg-transparent border-0 cursor-pointer hover:bg-bg3">
              <Copy size={13} className="text-t3" /> Copy
            </button>
            <button onClick={() => { setOpen(false); onForward(m); }} className="w-full flex items-center gap-[6px] text-left px-3 py-[7px] text-[12px] font-semibold text-t0 bg-transparent border-0 cursor-pointer hover:bg-bg3">
              <Forward size={13} className="text-t3" /> Forward
            </button>
            {onOpenThread && !m._optimistic && (
              <button onClick={() => { setOpen(false); onOpenThread(m); }} className="w-full flex items-center gap-[6px] text-left px-3 py-[7px] text-[12px] font-semibold text-t0 bg-transparent border-0 cursor-pointer hover:bg-bg3">
                <MessageSquareReply size={13} className="text-t3" /> Reply in thread
              </button>
            )}
            {onTogglePin && !m._optimistic && (
              <button onClick={() => { setOpen(false); onTogglePin(m.id, !!m.pinned); }} className="w-full flex items-center gap-[6px] text-left px-3 py-[7px] text-[12px] font-semibold text-t0 bg-transparent border-0 cursor-pointer hover:bg-bg3">
                {m.pinned ? <><PinOff size={13} className="text-t3" /> Unpin</> : <><Pin size={13} className="text-t3" /> Pin</>}
              </button>
            )}
            {onEdit && !m._optimistic && !m.attachment_kind && m.body && (Date.now() - new Date(m.created_at).getTime() < 10 * 60 * 1000) && (
              <button onClick={() => { setOpen(false); setEditErr(''); setEditing(m.body); }} className="w-full flex items-center gap-[6px] text-left px-3 py-[7px] text-[12px] font-semibold text-t0 bg-transparent border-0 cursor-pointer hover:bg-bg3">
                <Pencil size={13} className="text-t3" /> Edit
              </button>
            )}
            {onDelete && !m._optimistic && (
              <button onClick={async () => { setOpen(false); if (await confirmDialog('Delete this message for everyone?')) onDelete(m.id); }} className="w-full flex items-center gap-[6px] text-left px-3 py-[7px] text-[12px] font-semibold text-r bg-transparent border-0 cursor-pointer hover:bg-bg3">
                <Trash2 size={13} /> Delete
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// Pick a person or group to resend a message's text (or attachment link) to.
const ForwardPickerModal = ({ chat, message, onClose }) => {
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState([]); // [{kind, id, name}]
  const [sentCount, setSentCount] = useState(null);
  const text = forwardableText(message);
  const query = q.trim().toLowerCase();
  const people = chat.people.filter(p => (p.name || '').toLowerCase().includes(query));
  const groups = chat.groups.filter(g => (g.name || '').toLowerCase().includes(query));
  const isSelected = (kind, id) => selected.some(t => t.kind === kind && t.id === id);

  const toggle = (target) => setSelected(prev => isSelected(target.kind, target.id)
    ? prev.filter(t => !(t.kind === target.kind && t.id === target.id))
    : [...prev, target]);

  const sendAll = () => {
    if (!selected.length) return;
    for (const t of selected) {
      if (t.kind === 'dm') chat.sendMessage(t.id, text);
      else chat.sendGroupMessage(t.id, text);
    }
    setSentCount(selected.length);
    setTimeout(onClose, 700);
  };

  const Checkbox = ({ checked }) => (
    <div className={`w-[16px] h-[16px] rounded-[4px] border flex items-center justify-center flex-shrink-0 ${checked ? 'bg-p border-p' : 'border-b1'}`}>
      {checked && <Check size={11} color="#fff" />}
    </div>
  );

  return (
    <div className="fixed inset-0 z-[600] bg-black/40 flex items-center justify-center p-4" {...useBackdropDismiss(onClose)}>
      <div className="w-full max-w-[340px] max-h-[85vh] bg-bg1 border border-b0 rounded-2xl shadow-[0_18px_48px_rgba(15,23,42,0.3)] flex flex-col overflow-hidden animate-modalIn" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-[12px] border-b border-b0">
          <span className="text-[14px] font-extrabold text-t0">Forward message</span>
          <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer p-1 rounded-lg hover:bg-bg3"><X size={16} /></button>
        </div>
        {sentCount != null ? (
          <div className="p-6 text-center text-[13px] text-t2">Forwarded to {sentCount} {sentCount === 1 ? 'chat' : 'chats'}.</div>
        ) : (
          <>
            <div className="px-3 py-[8px] border-b border-b0">
              <Inp icon={Search} placeholder="Search people or groups..." value={q} onChange={setQ} bg="bg-bg2" />
            </div>
            <div className="flex-1 overflow-y-auto px-2 py-1">
              {groups.length > 0 && (
                <>
                  <div className="text-[10px] font-bold uppercase tracking-wide text-t3 px-3 pt-2 pb-1">Groups &amp; Channels</div>
                  {groups.map(g => (
                    <div key={'fg' + g.id} className="flex items-center gap-[8px] px-[6px]">
                      <Checkbox checked={isSelected('group', g.id)} />
                      <div className="flex-1 min-w-0">
                        <GroupRow g={g} onClick={() => toggle({ kind: 'group', id: g.id, name: g.name })} />
                      </div>
                    </div>
                  ))}
                  <div className="text-[10px] font-bold uppercase tracking-wide text-t3 px-3 pt-3 pb-1">People</div>
                </>
              )}
              {people.map(p => (
                <div key={'fp' + p.user_id} className="flex items-center gap-[8px] px-[6px]">
                  <Checkbox checked={isSelected('dm', p.user_id)} />
                  <div className="flex-1 min-w-0">
                    <PersonRow p={p} subtitle={p.designation || p.company || ''} onClick={() => toggle({ kind: 'dm', id: p.user_id, name: p.name })} />
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between gap-[8px] p-3 border-t border-b0">
              <span className="text-[11px] text-t3">{selected.length} selected</span>
              <button onClick={sendAll} disabled={!selected.length} className="px-4 py-[8px] rounded-[10px] bg-p text-white text-[12.5px] font-semibold border-0 cursor-pointer disabled:opacity-50">
                Forward{selected.length > 1 ? ` (${selected.length})` : ''}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Default to the next half-hour slot, 30 minutes long — the common case,
// with date/time/duration all still editable before sending.
const defaultMeetingSlot = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() < 30 ? 30 : 60, 0, 0);
  const pad = (n) => String(n).padStart(2, '0');
  return { date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`, time: `${pad(d.getHours())}:${pad(d.getMinutes())}` };
};

const ScheduleMeetingPanel = ({ sendFn, isAdmin, attendeeEmails: detectedEmails = [], people = [], groupMembers = null }) => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null); // null (loading) | {configured, connected, email, isOrgDefault, orgDefaultConnected, orgDefaultEmail}
  const [connecting, setConnecting] = useState(false);
  const [settingOrgDefault, setSettingOrgDefault] = useState(false);
  const [form, setForm] = useState(() => ({ title: 'Meeting', ...defaultMeetingSlot(), duration: 30 }));
  // Auto-detected attendee emails (from whoever already connected their own
  // Calendar) pre-fill this, but it's a plain editable field — adding
  // someone as a calendar attendee only needs their email, not any
  // connection/OAuth from them, so typing in an email we didn't detect
  // works exactly the same. Picking someone from `people` below just adds
  // their email into this same field; it's the single source of truth for
  // who gets invited.
  const [guestEmails, setGuestEmails] = useState(detectedEmails.join(', '));
  const [showPicker, setShowPicker] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { setGuestEmails(detectedEmails.join(', ')); }, [detectedEmails.join(',')]);

  const currentEmailList = () => guestEmails.split(',').map(e => e.trim()).filter(Boolean);
  const personEmail = (p) => p.google_email || p.email;
  const togglePerson = (p) => {
    const target = personEmail(p);
    if (!target) return;
    const list = currentEmailList();
    const next = list.includes(target) ? list.filter(e => e !== target) : [...list, target];
    setGuestEmails(next.join(', '));
  };
  const groupMemberIds = new Set((groupMembers || []).map(m => m.user_id));
  const pickerFiltered = people
    .filter(p => !groupMemberIds.has(p.user_id))
    .filter(p => (p.name || '').toLowerCase().includes(pickerQuery.trim().toLowerCase()));

  const [disconnecting, setDisconnecting] = useState(false);
  const disconnectCalendar = () => {
    if (!window.confirm('Disconnect your Google Calendar? You can connect again (even with a different account) afterwards.')) return;
    setDisconnecting(true);
    api.post('/api/google-calendar/disconnect', {})
      .then(() => api.get('/api/google-calendar/status'))
      .then(d => { if (d) setStatus(d); })
      .catch(() => setErr('Could not disconnect — try again.'))
      .finally(() => setDisconnecting(false));
  };

  const makeOrgDefault = () => {
    setSettingOrgDefault(true);
    api.post('/api/google-calendar/set-org-default', {})
      .then(() => api.get('/api/google-calendar/status'))
      .then(d => { if (d) setStatus(d); })
      .catch(() => setErr('Failed to set as the organization call calendar.'))
      .finally(() => setSettingOrgDefault(false));
  };

  const openPanel = () => {
    setOpen(true);
    setErr('');
    api.get('/api/google-calendar/status').then(d => { if (d) setStatus(d); }).catch(() => setStatus({ configured: false }));
  };

  const connect = () => {
    setConnecting(true);
    api.get('/auth/google/connect')
      .then(d => { if (d?.url) window.open(d.url, '_blank', 'noopener,noreferrer'); })
      .catch(() => setErr('Could not start the connection — try again.'))
      .finally(() => setConnecting(false));
  };

  const schedule = () => {
    if (!form.title.trim() || busy) return;
    const attendeeEmails = guestEmails.split(',').map(e => e.trim()).filter(e => e && e.includes('@'));
    setBusy(true);
    setErr('');
    const start = new Date(`${form.date}T${form.time}`);
    const end = new Date(start.getTime() + form.duration * 60000);
    api.post('/api/google-calendar/create-meeting', {
      summary: form.title.trim(), startTime: start.toISOString(), endTime: end.toISOString(), attendeeEmails,
    })
      .then(res => {
        if (!res?.hangoutLink) throw new Error('No meeting link returned');
        const when = start.toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
        const invited = attendeeEmails.length ? ' — invited on their calendar too' : '';
        sendFn(`📅 Scheduled "${form.title.trim()}" for ${when}${invited} — ${res.hangoutLink}`);
        setOpen(false);
      })
      .catch(e => setErr(e.message || 'Failed to schedule the meeting'))
      .finally(() => setBusy(false));
  };

  return (
    <div className="relative flex-shrink-0">
      <button
        onClick={() => (open ? setOpen(false) : openPanel())}
        className="w-[30px] h-[30px] rounded-[8px] bg-transparent text-t3 flex items-center justify-center border-0 cursor-pointer hover:bg-bg3 flex-shrink-0"
        title="Schedule a meeting"
      >
        <Calendar size={15} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-[40px] z-20 w-[250px] max-h-[380px] overflow-y-auto bg-bg1 border border-b0 rounded-[10px] shadow-[0_8px_24px_rgba(15,23,42,0.18)] p-3">
            {!status ? (
              <div className="text-[12px] text-t3 py-2 text-center">Loading…</div>
            ) : !status.configured ? (
              <div className="text-[11.5px] text-t2 leading-[1.4]">
                Google Calendar isn't connected yet. Ask your admin to set up Google OAuth credentials on the server to enable this.
              </div>
            ) : !status.connected ? (
              <>
                <div className="text-[12.5px] font-bold text-t0 mb-1">Connect your Google Calendar</div>
                <div className="text-[11.5px] text-t3 mb-2 leading-[1.4]">Meetings you schedule will be created on your own calendar, with an automatic Meet link.</div>
                <button onClick={connect} disabled={connecting} className="w-full py-[8px] rounded-[8px] bg-p text-white text-[12px] font-bold border-0 cursor-pointer disabled:opacity-60">
                  {connecting ? 'Opening…' : 'Connect Google Calendar'}
                </button>
              </>
            ) : (
              <>
                <div className="text-[12.5px] font-bold text-t0 mb-2">Schedule a meeting</div>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="text-[10px] text-t3">Connected as {status.email}</div>
                  <button onClick={disconnectCalendar} disabled={disconnecting} className="text-[10px] font-semibold text-r bg-transparent border-0 cursor-pointer hover:underline disabled:opacity-60 flex-shrink-0">
                    {disconnecting ? 'Disconnecting…' : 'Disconnect'}
                  </button>
                </div>
                {groupMembers && (
                  <>
                    <div className="text-[10px] text-t3 mb-1 leading-[1.4]">Group members — connected ones are pre-selected, click to add/remove anyone:</div>
                    <div className="max-h-[120px] overflow-y-auto border border-b0 rounded-[7px] mb-[8px]">
                      {groupMembers.map(p => {
                        const email = personEmail(p);
                        const checked = email && currentEmailList().includes(email);
                        return (
                          <button
                            key={p.user_id}
                            onClick={() => togglePerson(p)}
                            disabled={!email}
                            className="w-full flex items-center gap-[8px] px-[9px] py-[6px] bg-transparent border-0 cursor-pointer hover:bg-bg2 text-left disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <div className={`w-[14px] h-[14px] rounded-[4px] border flex items-center justify-center flex-shrink-0 ${checked ? 'bg-p border-p' : 'border-b1'}`}>
                              {checked && <Check size={10} color="#fff" />}
                            </div>
                            <span className="text-[11.5px] font-semibold text-t0 truncate flex-1">{p.name}</span>
                            <span className="text-[9.5px] text-t3 flex-shrink-0">{p.google_email ? 'connected' : (p.email ? 'no calendar' : '')}</span>
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
                <div className="text-[10px] text-t3 mb-1 leading-[1.4]">
                  {detectedEmails.length
                    ? "Edit the list below, or type any external email too, comma-separated."
                    : "Nobody here has connected a calendar yet, but you can still type a Google email below to book it directly — company or external."}
                </div>
                <input
                  value={guestEmails}
                  onChange={e => setGuestEmails(e.target.value)}
                  placeholder="their.email@gmail.com, external@anywhere.com"
                  className="w-full mb-[6px] py-[7px] px-[9px] bg-bg2 border border-b0 rounded-[7px] text-t0 text-[12px] outline-none"
                />
                <button
                  onClick={() => setShowPicker(v => !v)}
                  className="w-full mb-[8px] py-[6px] rounded-[7px] bg-bg2 border border-b0 text-t2 text-[11px] font-semibold cursor-pointer"
                >
                  {showPicker ? 'Hide' : `+ Invite more people from the org${groupMembers ? ' (outside this group)' : ''}`}
                </button>
                {showPicker && (
                  <div className="mb-[8px]">
                    <input
                      value={pickerQuery}
                      onChange={e => setPickerQuery(e.target.value)}
                      placeholder="Search people…"
                      className="w-full mb-[6px] py-[6px] px-[8px] bg-bg2 border border-b0 rounded-[7px] text-t0 text-[11.5px] outline-none"
                    />
                    <div className="max-h-[140px] overflow-y-auto border border-b0 rounded-[7px]">
                      {pickerFiltered.map(p => {
                        const email = personEmail(p);
                        const checked = email && currentEmailList().includes(email);
                        return (
                          <button
                            key={p.user_id}
                            onClick={() => togglePerson(p)}
                            disabled={!email}
                            className="w-full flex items-center gap-[8px] px-[9px] py-[6px] bg-transparent border-0 cursor-pointer hover:bg-bg2 text-left disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <div className={`w-[14px] h-[14px] rounded-[4px] border flex items-center justify-center flex-shrink-0 ${checked ? 'bg-p border-p' : 'border-b1'}`}>
                              {checked && <Check size={10} color="#fff" />}
                            </div>
                            <span className="text-[11.5px] font-semibold text-t0 truncate flex-1">{p.name}</span>
                            <span className="text-[9.5px] text-t3 flex-shrink-0">{p.google_email ? 'connected' : (p.email ? 'no calendar' : '')}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Meeting title" className="w-full mb-[6px] py-[7px] px-[9px] bg-bg2 border border-b0 rounded-[7px] text-t0 text-[12px] outline-none" />
                <div className="flex gap-[6px] mb-[6px]">
                  <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className="flex-1 py-[6px] px-[8px] bg-bg2 border border-b0 rounded-[7px] text-t0 text-[11.5px] outline-none" />
                  <input type="time" value={form.time} onChange={e => setForm(f => ({ ...f, time: e.target.value }))} className="w-[90px] py-[6px] px-[8px] bg-bg2 border border-b0 rounded-[7px] text-t0 text-[11.5px] outline-none" />
                </div>
                <select value={form.duration} onChange={e => setForm(f => ({ ...f, duration: Number(e.target.value) }))} className="w-full mb-[8px] py-[6px] px-[8px] bg-bg2 border border-b0 rounded-[7px] text-t0 text-[11.5px] outline-none">
                  {[15, 30, 45, 60, 90].map(m => <option key={m} value={m}>{m} minutes</option>)}
                </select>
                {err && <div className="text-[11px] text-r mb-[6px]">{err}</div>}
                <div className="sticky bottom-[-12px] -mx-3 -mb-3 px-3 pt-2 pb-3 bg-bg1 border-t border-b0"><button onClick={schedule} disabled={!form.title.trim() || busy} className="w-full py-[8px] rounded-[8px] bg-p text-white text-[12px] font-bold border-0 cursor-pointer disabled:opacity-50">
                  {busy ? 'Scheduling…' : 'Schedule & share link'}
                </button></div>
                {isAdmin && (
                  <div className="mt-[10px] pt-[10px] border-t border-b0">
                    {status.isOrgDefault ? (
                      <div className="text-[10.5px] text-t3 leading-[1.4]">✓ Your calendar is the organization's fallback — everyone's calls get a real Meet link automatically, even employees who haven't connected their own.</div>
                    ) : (
                      <>
                        <div className="text-[10.5px] text-t3 mb-[6px] leading-[1.4]">
                          {status.orgDefaultConnected
                            ? `Calls currently fall back to ${status.orgDefaultEmail}'s calendar for anyone who hasn't connected their own.`
                            : "No calendar connected yet for calls — everyone has to paste the Meet link manually until one is set."}
                        </div>
                        <button onClick={makeOrgDefault} disabled={settingOrgDefault} className="w-full py-[7px] rounded-[8px] bg-bg2 border border-b0 text-t1 text-[11px] font-bold cursor-pointer disabled:opacity-60">
                          {settingOrgDefault ? 'Setting…' : 'Use my calendar as the organization default'}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// Floating "incoming call" banner — rendered by ChatWidget at the app root
// (not inside ChatPanel) so it shows up even if the Messages panel itself
// is closed, the same way a phone rings regardless of which app is open.
// Resolves the caller's name from whatever's already loaded (people/
// conversations/group members) rather than trusting a client-sent name.
export const IncomingCallBanner = ({ chat }) => {
  const call = chat.incomingCall;
  const [muted, setMuted] = useState(false);
  useEffect(() => { setMuted(false); }, [call]);
  if (!call) return null;

  const callerName = chat.people.find(p => p.user_id === call.fromUserId)?.name
    || chat.conversations.find(c => c.user_id === call.fromUserId)?.name
    || chat.groupMembers.find(m => m.user_id === call.fromUserId)?.name
    || 'Someone';
  const groupName = call.groupId ? chat.groups.find(g => g.id === call.groupId)?.name : null;

  const mute = () => { chat.muteIncomingCall(); setMuted(true); };

  return (
    <div className="fixed top-5 left-1/2 -translate-x-1/2 z-[300] animate-dropIn">
      <div className="flex items-center gap-3 bg-bg1 border border-b0 rounded-2xl shadow-[0_18px_48px_rgba(15,23,42,0.28)] px-4 py-3 min-w-[320px]">
        <div className="w-[44px] h-[44px] rounded-full flex items-center justify-center flex-shrink-0 text-white animate-pulse" style={{ background: K.g }}>
          <Video size={20} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[13.5px] font-bold text-t0 truncate">{callerName} is calling{groupName ? ` · ${groupName}` : ''}</div>
          <div className="text-[11.5px] text-t3">Incoming video call</div>
        </div>
        <button onClick={mute} disabled={muted} className="w-[34px] h-[34px] rounded-full bg-bg2 text-t2 flex items-center justify-center border-0 cursor-pointer disabled:opacity-50 flex-shrink-0" title="Mute ring">
          {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
        </button>
        <button onClick={chat.declineCall} className="w-[34px] h-[34px] rounded-full bg-r text-white flex items-center justify-center border-0 cursor-pointer flex-shrink-0" title="Decline">
          <PhoneOff size={15} />
        </button>
        <button onClick={chat.answerCall} className="w-[34px] h-[34px] rounded-full bg-g text-white flex items-center justify-center border-0 cursor-pointer flex-shrink-0" title="Join">
          <Video size={15} />
        </button>
      </div>
    </div>
  );
};

// The caller's own feedback the moment they hit "Meet Now" — previously
// nothing showed here at all beyond the new tab and the chat message, which
// made a non-Calendar-connected call (the common case) look like it did
// nothing. Shares the ring-back tone's Mute control with the incoming
// banner; Cancel just dismisses this banner (the call itself, if any, was
// already started — there's nothing server-side to tear down).
export const OutgoingCallBanner = ({ chat }) => {
  const call = chat.outgoingCall;
  const [muted, setMuted] = useState(false);
  useEffect(() => { setMuted(false); }, [call]);
  if (!call) return null;

  const mute = () => { chat.muteOutgoingCall(); setMuted(true); };

  return (
    <div className="fixed top-5 left-1/2 -translate-x-1/2 z-[300] animate-dropIn">
      <div className="flex items-center gap-3 bg-bg1 border border-b0 rounded-2xl shadow-[0_18px_48px_rgba(15,23,42,0.28)] px-4 py-3 min-w-[320px]">
        <div className="w-[44px] h-[44px] rounded-full flex items-center justify-center flex-shrink-0 text-white animate-pulse" style={{ background: K.p }}>
          <Video size={20} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[13.5px] font-bold text-t0 truncate">Calling {call.targetName}…</div>
          <div className="text-[11.5px] text-t3">{call.meetLink ? 'Meet link shared automatically' : "No Meet link yet — connect Google Calendar to auto-share one"}</div>
        </div>
        <button onClick={mute} disabled={muted} className="w-[34px] h-[34px] rounded-full bg-bg2 text-t2 flex items-center justify-center border-0 cursor-pointer disabled:opacity-50 flex-shrink-0" title="Mute ring">
          {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
        </button>
        <button onClick={chat.cancelOutgoingCall} className="w-[34px] h-[34px] rounded-full bg-r text-white flex items-center justify-center border-0 cursor-pointer flex-shrink-0" title="Cancel">
          <PhoneOff size={15} />
        </button>
      </div>
    </div>
  );
};

// Lets any avatar inside the panel open the profile card without prop drilling.
const ProfileCtx = createContext(null);
const ClickableAv = ({ userId, nm, sz, src }) => {
  const ctx = useContext(ProfileCtx);
  const open = (e) => {
    e.stopPropagation();
    if (!ctx) return;
    ctx.open((ctx.people || []).find(p => String(p.user_id) === String(userId)) || { user_id: userId, name: nm, photo: src });
  };
  return (
    <button type="button" onClick={open} className="p-0 border-0 bg-transparent cursor-pointer flex-shrink-0 rounded-full" title="View profile">
      <Av nm={nm} sz={sz} rn={K.p} src={src} />
    </button>
  );
};

// Enlarged photo + basic profile, opened by clicking someone's avatar.
const PersonProfileModal = ({ p, onClose, onMessage }) => {
  const Line = ({ label, value }) => value ? (
    <div className="flex justify-between gap-3 py-[5px] border-b border-b0 text-[12.5px]">
      <span className="text-t3">{label}</span>
      <span className="text-t0 font-semibold text-right break-all">{value}</span>
    </div>
  ) : null;
  return (
    <div className="fixed inset-0 z-[600] bg-black/40 flex items-center justify-center p-4" {...useBackdropDismiss(onClose)}>
      <div className="w-full max-w-[320px] bg-bg1 border border-b0 rounded-2xl shadow-[0_18px_48px_rgba(15,23,42,0.3)] p-5 flex flex-col items-center" onClick={e => e.stopPropagation()}>
        <Av nm={p.name} sz={120} rn={K.p} src={p.photo} />
        <div className="mt-3 text-[16px] font-extrabold text-t0 text-center">{p.name}</div>
        {p.designation && <div className="text-[12px] text-t3 text-center">{p.designation}</div>}
        <div className="w-full mt-4">
          <Line label="Company" value={p.company} />
          <Line label="Office" value={p.office} />
          <Line label="Work email" value={p.email} />
          <Line label="Google Calendar" value={p.google_email ? 'Connected' : 'Not connected'} />
        </div>
        <div className="mt-4 flex items-center gap-2">
          {onMessage && p.user_id != null && (
            <button onClick={() => onMessage(p.user_id)} className="px-4 py-[7px] rounded-[8px] bg-p text-white border-0 text-[12px] font-semibold cursor-pointer hover:opacity-90">Message</button>
          )}
          <button onClick={onClose} className="px-4 py-[7px] rounded-[8px] bg-bg2 border border-b0 text-t0 text-[12px] font-semibold cursor-pointer hover:bg-bg3">Close</button>
        </div>
      </div>
    </div>
  );
};

const PersonRow = ({ p, subtitle, tick, unread, active, onClick, onTogglePin, onAvatarClick }) => (
  <div
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={e => { if (e.key === 'Enter') onClick(); }}
    className={`group w-full flex items-center gap-[10px] px-3 py-[9px] rounded-xl text-left cursor-pointer transition-colors ${active ? 'bg-pg' : 'bg-transparent hover:bg-bg3'}`}
  >
    <button
      type="button"
      onClick={e => { e.stopPropagation(); onAvatarClick?.(p); }}
      className="p-0 border-0 bg-transparent cursor-pointer flex-shrink-0 rounded-full"
      title="View profile"
    >
      <Av nm={p.name} sz={38} rn={K.p} src={p.photo} />
    </button>
    <div className="min-w-0 flex-1">
      <div className="text-[14px] font-bold text-t0 truncate flex items-center gap-1">
        {p.pinned && <Pin size={11} className="text-p flex-shrink-0 fill-current" />}
        <span className="truncate">{p.name}</span>
      </div>
      <div className="text-[12px] text-t3 truncate flex items-center gap-1">{tick}<span className="truncate">{subtitle}</span></div>
    </div>
    {onTogglePin && (
      <button
        onClick={e => { e.stopPropagation(); onTogglePin(p); }}
        className={`w-[22px] h-[22px] rounded-full bg-transparent border-0 cursor-pointer flex items-center justify-center flex-shrink-0 transition-opacity ${p.pinned ? 'text-p opacity-100' : 'text-t3 opacity-0 group-hover:opacity-100 hover:bg-bg2'}`}
        title={p.pinned ? 'Unpin' : 'Pin to top'}
      >
        {p.pinned ? <PinOff size={13} /> : <Pin size={13} />}
      </button>
    )}
    {unread > 0 && (
      <span className="min-w-[19px] h-[19px] px-[5px] rounded-full bg-r text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0">{unread}</span>
    )}
  </div>
);

const ListView = ({ chat, myUserId, onOpen, onOpenGroup, onNewAction, onProfile }) => {
  const [q, setQ] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [groupFilter, setGroupFilter] = useState('all'); // 'all' | 'group' | 'channel'
  const query = q.trim().toLowerCase();
  const filteredPeople = query
    ? chat.people.filter(p => (p.name || "").toLowerCase().includes(query))
    : chat.people;
  const filteredGroups = chat.groups
    .filter(g => groupFilter === 'all' || g.kind === groupFilter)
    .filter(g => !query || (g.name || "").toLowerCase().includes(query));
  const convById = Object.fromEntries(chat.conversations.map(c => [c.user_id, c]));

  const pick = (action) => { setMenuOpen(false); onNewAction(action); };

  return (
    <>
      <div className="px-3 py-[10px] border-b border-b0 flex items-center gap-[6px]">
        <div className="flex-1"><Inp icon={Search} placeholder="Search people, groups, channels..." value={q} onChange={setQ} bg="bg-bg2" /></div>
        <div className="relative flex-shrink-0">
          <button
            onClick={() => setMenuOpen(o => !o)}
            className="w-[34px] h-[34px] rounded-[10px] bg-p text-white flex items-center justify-center border-0 cursor-pointer flex-shrink-0"
            title="New group, channel, or browse channels"
          >
            <Plus size={16} />
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-0 top-[40px] z-20 w-[190px] bg-bg1 border border-b0 rounded-[10px] shadow-[0_8px_24px_rgba(15,23,42,0.18)] py-1">
                {[
                  { key: 'group', icon: Users, label: 'New Group' },
                  { key: 'channel', icon: Hash, label: 'New Channel' },
                  { key: 'browse', icon: ChevronDown, label: 'Browse Channels' },
                ].map(({ key, icon: Icon, label }) => (
                  <button
                    key={key}
                    onClick={() => pick(key)}
                    className="w-full flex items-center gap-[8px] px-3 py-[8px] text-[12.5px] font-semibold text-t0 bg-transparent border-0 cursor-pointer hover:bg-bg3 text-left"
                  >
                    <Icon size={14} className="text-t3" /> {label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
      {chat.groups.length > 0 && (
        <div className="flex gap-[6px] px-3 pt-[8px]">
          {[['all', 'All'], ['group', 'Groups'], ['channel', 'Channels']].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setGroupFilter(key)}
              className={`px-3 py-[4px] rounded-[7px] text-[11px] font-bold border-0 cursor-pointer transition-colors ${groupFilter === key ? 'bg-p text-white' : 'bg-bg2 text-t3 hover:bg-bg3'}`}
            >
              {label}
            </button>
          ))}
        </div>
      )}
      <div className="flex-1 overflow-y-auto px-2 py-1">
        {filteredGroups.length > 0 && (
          <>
            <div className="text-[11px] font-bold uppercase tracking-wide text-t3 px-3 pt-2 pb-1">Groups &amp; Channels</div>
            {filteredGroups.map(g => (
              <GroupRow
                key={'g' + g.id}
                g={g}
                active={chat.activeGroupId === g.id}
                onClick={() => onOpenGroup(g.id)}
                onTogglePin={grp => grp.pinned ? chat.unpinGroupConversation(grp.id) : chat.pinGroupConversation(grp.id)}
              />
            ))}
          </>
        )}
        {!query && chat.conversations.length > 0 && (
          <>
            <div className="text-[11px] font-bold uppercase tracking-wide text-t3 px-3 pt-3 pb-1">Recent</div>
            {chat.conversations.map(c => (
              <PersonRow
                key={c.user_id}
                p={c}
                subtitle={(c.last_message_mine ? 'You: ' : '') + (c.last_message || '')}
                tick={c.last_message_mine ? <Tick msg={{ sender_id: myUserId, read_at: c.last_message_read_at, delivered_at: c.last_message_delivered_at }} myUserId={myUserId} light /> : null}
                unread={c.unread_count}
                active={chat.activeUserId === c.user_id}
                onClick={() => onOpen(c.user_id)}
                onAvatarClick={onProfile}
                onTogglePin={person => person.pinned ? chat.unpinConversation(person.user_id) : chat.pinConversation(person.user_id)}
              />
            ))}
            <div className="text-[11px] font-bold uppercase tracking-wide text-t3 px-3 pt-3 pb-1">People</div>
          </>
        )}
        {filteredPeople.length === 0 && (
          <div className="text-[13px] text-t3 text-center py-8">No one found.</div>
        )}
        {filteredPeople.map(p => (
          <PersonRow
            key={p.user_id}
            p={p}
            subtitle={p.designation || p.company || ''}
            unread={query ? (convById[p.user_id]?.unread_count || 0) : 0}
            active={chat.activeUserId === p.user_id}
            onClick={() => onOpen(p.user_id)}
            onAvatarClick={onProfile}
          />
        ))}
      </div>
    </>
  );
};

// In-conversation "find" — pure client-side over whatever's already
// loaded, since the message list is small enough to just scan. Shared by
// the DM and group views so the match-navigation logic isn't duplicated.
function useInChatSearch(messages) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [matchIndex, setMatchIndex] = useState(0);
  const msgRefs = useRef({});

  const q = searchQuery.trim().toLowerCase();
  // Matches message text AND an attachment's filename — a shared document
  // like "Relieving Letter.docx" should be findable by name even though
  // it has no text body at all.
  const matches = q ? messages.filter(m =>
    (m.body || '').toLowerCase().includes(q) || (m.attachment_name || '').toLowerCase().includes(q)
  ) : [];
  const safeIndex = matches.length ? ((matchIndex % matches.length) + matches.length) % matches.length : 0;
  const activeMatchId = matches[safeIndex]?.id;

  useEffect(() => { setMatchIndex(0); }, [q]);

  useEffect(() => {
    if (activeMatchId == null) return;
    msgRefs.current[activeMatchId]?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [activeMatchId]);

  const close = () => { setSearchOpen(false); setSearchQuery(""); };

  return {
    searchOpen, setSearchOpen: (v) => { setSearchOpen(v); if (!v) setSearchQuery(""); }, close,
    searchQuery, setSearchQuery,
    matchCount: matches.length, matchPos: matches.length ? safeIndex + 1 : 0,
    goNext: () => setMatchIndex(i => i + 1), goPrev: () => setMatchIndex(i => i - 1),
    msgRefs, isActiveMatch: (id) => id === activeMatchId,
  };
}

// Small find-bar shown under the header when search is open — same shape
// for DM and group views.
const SearchBar = ({ s }) => (
  <div className="flex items-center gap-[6px] px-3 py-[8px] border-b border-b0 flex-shrink-0 bg-bg2">
    <Search size={14} className="text-t3 flex-shrink-0" />
    <input
      autoFocus
      value={s.searchQuery}
      onChange={e => s.setSearchQuery(e.target.value)}
      onKeyDown={e => {
        if (e.key === 'Enter') { e.preventDefault(); e.shiftKey ? s.goPrev() : s.goNext(); }
        if (e.key === 'Escape') { e.stopPropagation(); s.close(); }
      }}
      placeholder="Search in this conversation..."
      className="flex-1 bg-transparent border-0 outline-none text-t0 text-[13px]"
    />
    <span className="text-[11px] text-t3 flex-shrink-0 tabular-nums">{s.matchCount ? `${s.matchPos}/${s.matchCount}` : (s.searchQuery.trim() ? '0/0' : '')}</span>
    <button onClick={s.goPrev} disabled={!s.matchCount} className="w-[22px] h-[22px] rounded-[6px] bg-transparent border-0 text-t2 flex items-center justify-center cursor-pointer hover:bg-bg3 disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0" title="Previous match">
      <ChevronUp size={14} />
    </button>
    <button onClick={s.goNext} disabled={!s.matchCount} className="w-[22px] h-[22px] rounded-[6px] bg-transparent border-0 text-t2 flex items-center justify-center cursor-pointer hover:bg-bg3 disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0" title="Next match">
      <ChevronDown size={14} />
    </button>
    <button onClick={s.close} className="w-[22px] h-[22px] rounded-[6px] bg-transparent border-0 text-t3 flex items-center justify-center cursor-pointer hover:bg-bg3 flex-shrink-0" title="Close search">
      <X size={14} />
    </button>
  </div>
);

// A text selection that starts inside the dialog and ends on the dim
// background fires a click on the background, which used to close the whole
// dialog. Only treat it as a dismiss when the press itself began there too.
const useBackdropDismiss = (onClose) => {
  const pressedOnBackdrop = useRef(false);
  return {
    onMouseDown: (e) => { pressedOnBackdrop.current = e.target === e.currentTarget; },
    onClick: (e) => { if (pressedOnBackdrop.current && e.target === e.currentTarget) onClose(); },
  };
};

const ATTACHMENT_ACCEPT = "*/*";
const attachmentKindFor = (file) => {
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.startsWith('audio/')) return 'audio';
  return 'file';
};

// WhatsApp-style staging: picked files wait in a tray above the composer
// (with a caption typed in the normal message box) and are only uploaded
// when Send is pressed. Several files can be staged at once.
const useStagedFiles = () => {
  const [files, setFiles] = useState([]);
  const ref = useRef([]);
  const sync = (next) => { ref.current = next; setFiles(next); };
  const add = (list) => {
    const added = Array.from(list || []).map((file, i) => ({
      id: `${Date.now()}-${i}-${Math.random().toString(36).slice(2, 7)}`,
      file,
      url: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
    }));
    if (added.length) sync([...ref.current, ...added]);
  };
  const remove = (id) => {
    const hit = ref.current.find(f => f.id === id);
    if (hit?.url) URL.revokeObjectURL(hit.url);
    sync(ref.current.filter(f => f.id !== id));
  };
  const clear = () => {
    ref.current.forEach(f => { if (f.url) URL.revokeObjectURL(f.url); });
    sync([]);
  };
  useEffect(() => () => ref.current.forEach(f => { if (f.url) URL.revokeObjectURL(f.url); }), []);
  return { files, add, remove, clear };
};

const StagedFilesBar = ({ staged, onAdd }) => {
  if (!staged.files.length) return null;
  return (
    <div className="flex items-center gap-2 px-3 pt-[10px] pb-[2px] border-t border-b0 overflow-x-auto">
      {staged.files.map(f => (
        <div key={f.id} className="relative flex-shrink-0 w-[64px] h-[64px] rounded-[10px] border border-b0 bg-bg2 overflow-hidden flex flex-col items-center justify-center" title={`${f.file.name} · ${fmtBytes(f.file.size)}`}>
          {f.url
            ? <img src={f.url} alt="" className="w-full h-full object-cover" />
            : <>
                <FileText size={20} className="text-t2" />
                <div className="text-[9px] text-t3 px-1 w-full truncate text-center mt-[2px]">{f.file.name}</div>
              </>}
          <button onClick={() => staged.remove(f.id)} className="absolute top-[2px] right-[2px] w-[16px] h-[16px] rounded-full bg-black/60 text-white border-0 cursor-pointer flex items-center justify-center p-0" title="Remove">
            <X size={10} />
          </button>
        </div>
      ))}
      <button onClick={onAdd} className="flex-shrink-0 w-[64px] h-[64px] rounded-[10px] border border-dashed border-b0 bg-transparent text-t2 cursor-pointer flex items-center justify-center hover:bg-bg3" title="Add more files">
        <Plus size={20} />
      </button>
    </div>
  );
};

// Shown in the right pane before any conversation is selected — the list
// (left pane) is always visible, so this just prompts a choice rather than
// repeating the "say hi" mascot used for a picked-but-empty thread.
const NoThreadSelected = () => (
  <div className="flex-1 flex flex-col items-center justify-center text-center px-6 gap-2">
    <div className="text-[32px] leading-none opacity-60">💬</div>
    <div className="text-[13px] text-t3">Select a conversation to start messaging.</div>
  </div>
);

// Shown when the Threads tab has nothing in it yet at all.
const NoThreadsYet = () => (
  <div className="flex-1 flex flex-col items-center justify-center text-center px-6 gap-2">
    <MessageSquareReply size={28} className="text-t3 opacity-60" />
    <div className="text-[12px] font-semibold text-t1">No threads yet</div>
    <div className="text-[11px] text-t3 max-w-[160px] leading-[1.4]">Use "Reply in thread" on any message to start one.</div>
  </div>
);

// Shown in the right pane when threads exist but none is open.
const NoThreadOpen = () => (
  <div className="flex-1 flex flex-col items-center justify-center text-center px-6 gap-2">
    <div className="text-[32px] leading-none opacity-60">💬</div>
    <div className="text-[13px] text-t3">Select a thread to view it.</div>
  </div>
);

const ThreadsListView = ({ chat }) => (
  <div className="flex-1 overflow-y-auto px-2 py-1">
    {chat.threads.length === 0 && (
      <div className="text-[12px] text-t3 text-center py-8 px-4 leading-[1.4]">No threads yet — use "Reply in thread" on any message.</div>
    )}
    {chat.threads.map(t => {
      const unread = !t.last_read_at || new Date(t.last_reply_at) > new Date(t.last_read_at);
      const snippet = t.root_body || (t.root_attachment_kind ? `📎 ${t.root_attachment_kind}` : 'Message');
      return (
        <button
          key={t.root_id}
          onClick={() => chat.openThreadView(t.root_id)}
          className={`w-full flex items-center gap-[10px] px-3 py-[9px] rounded-xl text-left border-0 cursor-pointer transition-colors ${chat.activeThreadRootId === t.root_id ? 'bg-pg' : 'bg-transparent hover:bg-bg3'}`}
        >
          <div className="w-[36px] h-[36px] rounded-full flex items-center justify-center flex-shrink-0 text-white" style={{ background: t.group_id ? K.tl : K.pu }}>
            <MessageSquareReply size={15} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-bold text-t0 truncate">{t.group_name || t.root_sender_name}</div>
            <div className="text-[11px] text-t3 truncate">{snippet} · {t.reply_count} repl{t.reply_count === 1 ? 'y' : 'ies'}</div>
          </div>
          {unread && <span className="w-[8px] h-[8px] rounded-full bg-r flex-shrink-0" />}
        </button>
      );
    })}
  </div>
);

const ThreadDetailView = ({ chat, myUserId }) => {
  const [text, setText] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [recordSecs, setRecordSecs] = useState(0);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const staged = useStagedFiles();
  const recorderRef = useRef(null);
  const recordCancelRef = useRef(false);
  const recordChunksRef = useRef([]);
  const recordStreamRef = useRef(null);
  const recordTimerRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [chat.threadMessages]);

  useEffect(() => () => {
    clearInterval(recordTimerRef.current);
    recordStreamRef.current?.getTracks().forEach(t => t.stop());
  }, [chat.activeThreadRootId]);

  const root = chat.threadMessages[0];
  const replies = chat.threadMessages.slice(1);

  const send = () => {
    if (staged.files.length) {
      const caption = text.trim();
      staged.files.forEach((f, i) => chat.sendThreadReplyAttachment(f.file, attachmentKindFor(f.file), i === 0 ? caption : ''));
      staged.clear();
      setText("");
      return;
    }
    if (!text.trim()) return;
    chat.sendThreadReply(text);
    setText("");
  };

  const onPickFile = (e) => {
    const picked = e.target.files;
    staged.add(picked);
    e.target.value = '';
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recordStreamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recordChunksRef.current = [];
      recordCancelRef.current = false;
      recorder.ondataavailable = (e) => { if (e.data.size > 0) recordChunksRef.current.push(e.data); };
      recorder.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        if (recordCancelRef.current) return; // Cancel: discard, never send
        const blob = new Blob(recordChunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        if (blob.size > 0) {
          const file = new File([blob], 'voice-message.webm', { type: blob.type });
          chat.sendThreadReplyAttachment(file, 'audio');
        }
      };
      recorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
      setRecordSecs(0);
      recordTimerRef.current = setInterval(() => setRecordSecs(s => s + 1), 1000);
    } catch (err) {
      console.error('Mic access denied or unavailable:', err);
    }
  };

  const stopRecording = (cancel = false) => {
    clearInterval(recordTimerRef.current);
    setIsRecording(false);
    recordCancelRef.current = cancel;
    if (cancel) recordChunksRef.current = [];
    recorderRef.current?.stop();
  };

  const fmtSecs = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  if (!root) return <div className="flex-1 flex items-center justify-center text-[13px] text-t3">Loading…</div>;

  return (
    <>
      <div className="flex items-center gap-2 px-3 py-[10px] border-b border-b0 flex-shrink-0">
        <MessageSquareReply size={16} className="text-t3" />
        <div className="text-[14px] font-bold text-t0 truncate flex-1">Thread</div>
      </div>
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-3 py-3 flex flex-col gap-[6px]"
        style={{ backgroundColor: K.pg, backgroundImage: `radial-gradient(${K.p}22 1px, transparent 1px)`, backgroundSize: '18px 18px' }}
      >
        <div className="bg-bg1 border border-b0 rounded-2xl p-3">
          <div className="flex items-center gap-2 mb-1">
            <ClickableAv userId={root.sender_id} nm={root.sender_name} sz={24} src={root.sender_photo} />
            <span className="text-[12.5px] font-bold text-t0">{root.sender_name}</span>
            <span className="text-[10px] text-t3 ml-auto">{msgTime(root.created_at)}</span>
          </div>
          {root.attachment_kind && <div className="mt-1"><Attachment m={root} /></div>}
          {root.body && <div className="text-[13px] text-t0 whitespace-pre-wrap break-words mt-1">{renderMessageBody(root.body)}</div>}
        </div>
        <div className="text-[10px] font-bold uppercase tracking-wide text-t3 px-1 pt-1">
          {replies.length} repl{replies.length === 1 ? 'y' : 'ies'}
        </div>
        {replies.map((m, idx) => {
          const mine = m.sender_id === myUserId;
          const hasAttachment = !!m.attachment_kind;
          const bubbleClass = hasAttachment
            ? `bg-bg1 border border-b0 text-t0 ${m.attachment_kind === 'image' ? 'p-[3px]' : 'p-2'} ${mine ? 'rounded-br-[4px]' : 'rounded-bl-[4px]'}`
            : `px-3 py-[7px] ${mine ? 'bg-pl text-white rounded-br-[4px]' : 'bg-bg3 text-t0 rounded-bl-[4px]'}`;
          return (
            <div key={m.id || idx} className={`flex items-end gap-[6px] ${mine ? 'justify-end' : 'justify-start'}`}>
              {!mine && <ClickableAv userId={m.sender_id} nm={m.sender_name} sz={22} src={m.sender_photo} />}
              <div className={`max-w-[75%] rounded-2xl text-[12.5px] leading-[1.4] ${bubbleClass}`}>
                {!mine && <div className="text-[10.5px] font-bold px-3 pt-[5px]" style={{ color: K.p }}>{m.sender_name}</div>}
                {hasAttachment && (
                  <div className={`${m._uploading ? 'opacity-60' : ''} ${!mine ? 'px-3 pb-[2px]' : ''}`}>
                    <Attachment m={m} />
                  </div>
                )}
                {m.body && (
                  <div className={`break-words whitespace-pre-wrap ${hasAttachment ? 'px-[6px] pt-[6px]' : (!mine ? 'px-3' : '')}`}>
                    {renderMessageBody(m.body, { mine })}
                  </div>
                )}
                <div className={`text-[9px] text-right ${hasAttachment ? 'text-t3 px-[6px] pb-[3px] pt-[2px]' : (mine ? 'text-white/75 pr-3 pb-[5px]' : 'text-t3 px-3 pb-[5px]')}`}>
                  {m._uploading && <span>Uploading… </span>}
                  {m._failed && <span>Failed </span>}
                  {msgTime(m.created_at)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <StagedFilesBar staged={staged} onAdd={() => fileInputRef.current?.click()} />
      <div className={`flex items-center gap-2 px-3 ${staged.files.length ? 'pt-[6px] pb-[10px]' : 'py-[10px] border-t border-b0'}`}>
        <input ref={fileInputRef} type="file" multiple accept={ATTACHMENT_ACCEPT} onChange={onPickFile} className="hidden" />
        {isRecording ? (
          <div className="flex-1 flex items-center gap-2 py-[8px] px-[12px] bg-bg2 border border-b0 rounded-[10px]">
            <span className="w-[8px] h-[8px] rounded-full bg-r animate-pulse flex-shrink-0" />
            <span className="text-[13px] text-t0 flex-1">Recording… {fmtSecs(recordSecs)}</span>
            <button onClick={() => stopRecording(true)} className="bg-transparent border-0 text-t3 cursor-pointer p-0 text-[12px] font-semibold">Cancel</button>
          </div>
        ) : (
          <>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-[34px] h-[34px] rounded-[10px] bg-transparent text-t2 flex items-center justify-center border-0 cursor-pointer hover:bg-bg3 flex-shrink-0"
              title="Send a file or image"
            >
              <Paperclip size={16} />
            </button>
            <input
              value={text}
              onChange={e => setText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Reply in thread..."
              className="flex-1 py-[8px] px-[12px] bg-bg2 border border-b0 rounded-[10px] text-t0 text-[13px] outline-none"
            />
          </>
        )}
        {!isRecording && !text.trim() && !staged.files.length ? (
          <button
            onClick={startRecording}
            className="w-[34px] h-[34px] rounded-[10px] bg-p text-white flex items-center justify-center border-0 cursor-pointer flex-shrink-0"
            title="Record a voice message"
          >
            <Mic size={15} />
          </button>
        ) : (
          <button
            onClick={isRecording ? () => stopRecording(false) : send}
            disabled={!isRecording && !text.trim() && !staged.files.length}
            className="w-[34px] h-[34px] rounded-[10px] bg-p text-white flex items-center justify-center border-0 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
          >
            {isRecording ? <Square size={14} /> : <Send size={15} />}
          </button>
        )}
      </div>
    </>
  );
};

const ThreadView = ({ chat, myUserId, activePerson, onForward, onOpenThread, isAdmin, onProfile }) => {
  const [text, setText] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [recordSecs, setRecordSecs] = useState(0);
  const [replyingTo, setReplyingTo] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const s = useInChatSearch(chat.messages);
  const typingTimeoutRef = useRef(null);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const staged = useStagedFiles();
  const recorderRef = useRef(null);
  const recordCancelRef = useRef(false);
  const recordChunksRef = useRef([]);
  const recordStreamRef = useRef(null);
  const recordTimerRef = useRef(null);
  const isTyping = !!chat.typingFrom[chat.activeUserId];

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [chat.messages, isTyping]);

  // Stop any in-flight recording/mic stream if the thread is switched or
  // the widget closes mid-recording — otherwise the mic stays "hot".
  useEffect(() => {
    setReplyingTo(null);
    staged.clear();
    return () => {
      clearInterval(recordTimerRef.current);
      recordStreamRef.current?.getTracks().forEach(t => t.stop());
    };
  }, [chat.activeUserId]);

  const handleChange = (val) => {
    setText(val);
    chat.setTyping(chat.activeUserId, true);
    clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => chat.setTyping(chat.activeUserId, false), 1500);
  };

  // "Reply" (inline quote, stays in this same chat) is distinct from
  // "Reply in thread" (onOpenThread, a separate Threads-tab conversation).
  const startReply = (m) => setReplyingTo({
    id: m.id, body: m.body, attachment_kind: m.attachment_kind, attachment_name: m.attachment_name,
    sender_name: m.sender_id === myUserId ? 'You' : (activePerson?.name || 'them'),
  });

  // Jumps to and briefly highlights the quoted original — only works if
  // it's still in the currently loaded page of this same chat, same as
  // WhatsApp's own quote-tap behavior. Reuses the same per-message ref map
  // the in-chat search jump-to-result feature already keeps.
  const scrollToMessage = (id) => {
    const el = s.msgRefs.current[id];
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightId(id);
    setTimeout(() => setHighlightId(null), 1500);
  };

  const send = () => {
    if (staged.files.length) {
      const caption = text.trim();
      staged.files.forEach((f, i) => chat.sendAttachment(chat.activeUserId, f.file, attachmentKindFor(f.file), i === 0 ? caption : '', i === 0 ? replyingTo : null));
      staged.clear();
      setText("");
      setReplyingTo(null);
      clearTimeout(typingTimeoutRef.current);
      chat.setTyping(chat.activeUserId, false);
      return;
    }
    if (!text.trim()) return;
    chat.sendMessage(chat.activeUserId, text, replyingTo);
    setText("");
    setReplyingTo(null);
    clearTimeout(typingTimeoutRef.current);
    chat.setTyping(chat.activeUserId, false);
  };

  const onPickFile = (e) => {
    const picked = e.target.files;
    staged.add(picked);
    e.target.value = '';
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recordStreamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recordChunksRef.current = [];
      recordCancelRef.current = false;
      recorder.ondataavailable = (e) => { if (e.data.size > 0) recordChunksRef.current.push(e.data); };
      recorder.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        if (recordCancelRef.current) return; // Cancel: discard, never send
        const blob = new Blob(recordChunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        if (blob.size > 0) {
          const file = new File([blob], 'voice-message.webm', { type: blob.type });
          chat.sendAttachment(chat.activeUserId, file, 'audio', '', replyingTo);
          setReplyingTo(null);
        }
      };
      recorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
      setRecordSecs(0);
      recordTimerRef.current = setInterval(() => setRecordSecs(s => s + 1), 1000);
    } catch (err) {
      console.error('Mic access denied or unavailable:', err);
    }
  };

  const stopRecording = (cancel = false) => {
    clearInterval(recordTimerRef.current);
    setIsRecording(false);
    recordCancelRef.current = cancel;
    if (cancel) recordChunksRef.current = [];
    recorderRef.current?.stop();
  };

  const fmtSecs = (secs) => `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;

  return (
    <>
      <div className="flex items-center gap-2 px-3 py-[10px] border-b border-b0 flex-shrink-0">
        <button type="button" onClick={onProfile} className="relative flex-shrink-0 p-0 border-0 bg-transparent cursor-pointer rounded-full" title="View profile">
          <Av nm={activePerson?.name} sz={32} rn={K.p} src={activePerson?.photo} />
          {activePerson?.google_email && (
            activePerson.email === 'vineet.saraswat@zucol.in' ? (
              <div
                className="absolute -bottom-[2px] -right-[2px] w-[13px] h-[13px] rounded-full border-2 border-bg1 flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #FFF3B0 0%, #FFD700 35%, #D4AF37 65%, #B8860B 100%)', boxShadow: '0 0 4px rgba(212,175,55,0.9)' }}
                title="Connected with calendar — HRMS developer"
              >
                <Check size={8} color="#5c4600" strokeWidth={3.5} />
              </div>
            ) : (
              <div
                className="absolute -bottom-[2px] -right-[2px] w-[13px] h-[13px] rounded-full bg-p border-2 border-bg1 flex items-center justify-center"
                title={`Connected with calendar (${activePerson.google_email})`}
              >
                <Check size={8} color="#fff" strokeWidth={3} />
              </div>
            )
          )}
        </button>
        <div className="text-[14px] font-bold text-t0 truncate flex-1">{activePerson?.name || '...'}</div>
        <button
          onClick={() => s.setSearchOpen(!s.searchOpen)}
          className={`w-[30px] h-[30px] rounded-[8px] bg-transparent flex items-center justify-center border-0 cursor-pointer hover:bg-bg3 flex-shrink-0 ${s.searchOpen ? 'text-p' : 'text-t3'}`}
          title="Search in this conversation"
        >
          <Search size={15} />
        </button>
        <button
          onClick={() => chat.startCall({ type: 'dm', toUserId: chat.activeUserId, name: activePerson?.name })}
          className="w-[30px] h-[30px] rounded-[8px] bg-transparent text-t3 flex items-center justify-center border-0 cursor-pointer hover:bg-bg3 flex-shrink-0"
          title="Start a video call"
        >
          <Video size={15} />
        </button>
        <ScheduleMeetingPanel
          sendFn={(text) => chat.sendMessage(chat.activeUserId, text)}
          isAdmin={isAdmin}
          attendeeEmails={activePerson?.google_email ? [activePerson.google_email] : []}
          people={chat.people}
        />
      </div>
      {s.searchOpen && <SearchBar s={s} />}
      <PinnedMessagesBar chat={chat} />
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-3 py-3 flex flex-col gap-[2px]"
        style={{ backgroundColor: K.pg, backgroundImage: `radial-gradient(${K.p}22 1px, transparent 1px)`, backgroundSize: '18px 18px' }}
      >
        {chat.messages.length === 0 && !isTyping && <EmptyThread person={activePerson} />}
        {chat.messages.map((m, idx) => {
          const mine = m.sender_id === myUserId;
          const hasAttachment = !!m.attachment_kind;
          // Tight spacing within a run of consecutive messages from the same
          // sender, more breathing room between runs — otherwise several
          // blue "mine" bubbles in a row just read as one solid blue block.
          const isNewRun = idx === 0 || chat.messages[idx - 1].sender_id !== m.sender_id;
          // Attachments get a neutral card (never the solid blue "mine"
          // fill) — only a plain-text bubble keeps the colored background.
          // "Mine" text bubbles use the softer bg-pl, not the vivid bg-p,
          // for the same reason — a run of several reads as too heavy.
          const bubbleClass = hasAttachment
            ? `bg-bg1 border border-b0 text-t0 ${m.attachment_kind === 'image' ? 'p-[3px]' : 'p-2'} ${mine ? 'rounded-br-[4px]' : 'rounded-bl-[4px]'}`
            : `px-3 py-[7px] ${mine ? 'bg-pl text-white rounded-br-[4px]' : 'bg-bg3 text-t0 rounded-bl-[4px]'}`;
          const dayPill = (idx === 0 || dayKeyOf(chat.messages[idx - 1].created_at) !== dayKeyOf(m.created_at)) ? <DaySeparator ts={m.created_at} /> : null;
          if (m.system_event) return <React.Fragment key={m.id || idx}>{dayPill}<SystemLine m={m} /></React.Fragment>;
          if (m.deleted_at) return <React.Fragment key={m.id || idx}>{dayPill}<DeletedLine m={m} mine={mine} isNewRun={isNewRun} /></React.Fragment>;
          return (
            <React.Fragment key={m.id || idx}>{dayPill}
            <div
              key={m.id || idx}
              ref={el => { s.msgRefs.current[m.id] = el; }}
              className={`group flex items-end gap-1 ${mine ? 'justify-end' : 'justify-start'} ${isNewRun ? 'mt-[8px]' : ''} ${(s.isActiveMatch(m.id) || highlightId === m.id) ? 'bg-ab/60 -mx-2 px-2 rounded-xl' : ''}`}
            >
              {mine && <MessageActionsMenu m={m} onForward={onForward} onOpenThread={onOpenThread} onReact={(id, emoji) => chat.toggleReaction(id, emoji)} onTogglePin={(id, pinned) => chat.togglePinMessage(id, pinned)} onDelete={mine ? (id) => chat.deleteMessage(id) : undefined} onEdit={mine ? (id, body) => chat.editMessage(id, body) : undefined} onReply={startReply} className="mb-[4px]" />}
              <div className={`flex flex-col max-w-[75%] ${mine ? 'items-end' : 'items-start'}`}>
                <div className={`rounded-2xl text-[13px] leading-[1.4] ${bubbleClass}`}>
                  <QuotedMessageBlock m={m} mine={mine} onClick={scrollToMessage} />
                  {hasAttachment && (
                    <div className={m._uploading ? 'opacity-60' : ''}>
                      <Attachment m={m} />
                    </div>
                  )}
                  {m.body && <div className={`break-words whitespace-pre-wrap ${hasAttachment ? 'px-[6px] pt-[6px]' : ''}`}>{renderMessageBody(m.body, { mine })}</div>}
                  <div className={`flex items-center gap-1 justify-end text-[10px] ${hasAttachment ? 'text-t3 px-[6px] pt-[3px]' : (mine ? 'text-white/75 mt-[3px]' : 'text-t3 mt-[3px]')}`}>
                    {m._uploading && <span>Uploading…</span>}
                    {m._failed && <span>Failed to send</span>}
                    {m.edited_at && <span>Edited</span>}<span>{msgTime(m.created_at)}</span>
                    <Tick msg={m} myUserId={myUserId} light={hasAttachment} />
                  </div>
                </div>
                <ReactionBar m={m} onReact={chat.toggleReaction} mine={mine} />
              </div>
              {!mine && <MessageActionsMenu m={m} onForward={onForward} onOpenThread={onOpenThread} onReact={(id, emoji) => chat.toggleReaction(id, emoji)} onTogglePin={(id, pinned) => chat.togglePinMessage(id, pinned)} onDelete={mine ? (id) => chat.deleteMessage(id) : undefined} onEdit={mine ? (id, body) => chat.editMessage(id, body) : undefined} onReply={startReply} className="mb-[4px]" />}
            </div>
          </React.Fragment>
          );
        })}
        {isTyping && <TypingDots />}
      </div>
      <ReplyPreviewBar replyingTo={replyingTo} onCancel={() => setReplyingTo(null)} />
      <StagedFilesBar staged={staged} onAdd={() => fileInputRef.current?.click()} />
      <div className={`flex items-center gap-2 px-3 ${staged.files.length ? 'pt-[6px] pb-[10px]' : 'py-[10px] border-t border-b0'}`}>
        <input ref={fileInputRef} type="file" multiple accept={ATTACHMENT_ACCEPT} onChange={onPickFile} className="hidden" />
        {isRecording ? (
          <div className="flex-1 flex items-center gap-2 py-[8px] px-[12px] bg-bg2 border border-b0 rounded-[10px]">
            <span className="w-[8px] h-[8px] rounded-full bg-r animate-pulse flex-shrink-0" />
            <span className="text-[13px] text-t0 flex-1">Recording… {fmtSecs(recordSecs)}</span>
            <button onClick={() => stopRecording(true)} className="bg-transparent border-0 text-t3 cursor-pointer p-0 text-[12px] font-semibold">Cancel</button>
          </div>
        ) : (
          <>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-[34px] h-[34px] rounded-[10px] bg-transparent text-t2 flex items-center justify-center border-0 cursor-pointer hover:bg-bg3 flex-shrink-0"
              title="Send a file or image"
            >
              <Paperclip size={16} />
            </button>
            <input
              value={text}
              onChange={e => handleChange(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Type a message..."
              data-esc-clears
              className="flex-1 py-[8px] px-[12px] bg-bg2 border border-b0 rounded-[10px] text-t0 text-[13px] outline-none"
            />
          </>
        )}
        {!isRecording && !text.trim() && !staged.files.length ? (
          <button
            onClick={startRecording}
            className="w-[34px] h-[34px] rounded-[10px] bg-p text-white flex items-center justify-center border-0 cursor-pointer flex-shrink-0"
            title="Record a voice message"
          >
            <Mic size={15} />
          </button>
        ) : (
          <button
            onClick={isRecording ? () => stopRecording(false) : send}
            disabled={!isRecording && !text.trim() && !staged.files.length}
            className="w-[34px] h-[34px] rounded-[10px] bg-p text-white flex items-center justify-center border-0 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
          >
            {isRecording ? <Square size={14} /> : <Send size={15} />}
          </button>
        )}
      </div>
    </>
  );
};

const MemberList = ({ members, myUserId, isGroupOwner, onPromote, onDemote, onRemove }) => {
  const [busyId, setBusyId] = useState(null);
  const run = (userId, action) => {
    setBusyId(userId);
    Promise.resolve(action(userId)).finally(() => setBusyId(null));
  };
  const removeMember = async (m) => {
    if (!(await confirmDialog(`Remove ${m.name} from this ${m.is_guest ? 'conversation' : 'group'}?`))) return;
    run(m.user_id, onRemove);
  };
  return (
    <div className="absolute right-3 top-[52px] z-20 w-[260px] max-h-[300px] overflow-y-auto bg-bg1 border border-b0 rounded-[10px] shadow-[0_8px_24px_rgba(15,23,42,0.18)] py-1">
      {members.map(m => {
        const isSelf = m.user_id === myUserId;
        const busy = busyId === m.user_id;
        return (
          <div key={m.user_id} className="flex items-center gap-[8px] px-3 py-[7px]">
            <ClickableAv userId={m.user_id} nm={m.name} sz={26} src={m.photo} />
            <div className="min-w-0 flex-1">
              <div className="text-[12.5px] font-semibold text-t0 truncate">{m.name}{m.is_guest ? ' (Guest)' : ''}{isSelf ? ' (You)' : ''}</div>
              <div className="text-[10px] text-t3">{m.role === 'owner' ? 'Admin' : 'Member'}</div>
            </div>
            {isGroupOwner && !isSelf && (
              <div className="flex items-center gap-[6px] flex-shrink-0">
                {m.role === 'owner' ? (
                  <button onClick={() => run(m.user_id, onDemote)} disabled={busy} className="text-[10px] font-semibold text-t3 bg-transparent border-0 cursor-pointer disabled:opacity-50" title="Remove admin rights">
                    {busy ? '…' : 'Remove Admin'}
                  </button>
                ) : !m.is_guest && (
                  <button onClick={() => run(m.user_id, onPromote)} disabled={busy} className="flex items-center gap-[3px] text-[10.5px] font-semibold text-p bg-transparent border-0 cursor-pointer disabled:opacity-50" title="Make this person an admin of this group">
                    <ShieldCheck size={11} />{busy ? '…' : 'Make Admin'}
                  </button>
                )}
                <button onClick={() => removeMember(m)} disabled={busy} className="w-[22px] h-[22px] rounded-full bg-transparent text-r flex items-center justify-center border-0 cursor-pointer disabled:opacity-50" title="Remove from this group">
                  <UserMinus size={13} />
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Owner-only: rename the group/channel or disband it entirely. Any owner has
// identical rights here — there's no single "super owner" above the others.
const GroupSettingsPanel = ({ chat, group, onClose }) => {
  const [name, setName] = useState(group?.name || '');
  const [description, setDescription] = useState(group?.description || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [iconBusy, setIconBusy] = useState(false);
  const iconInputRef = useRef(null);

  const pickIcon = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setIconBusy(true);
    setErr('');
    chat.setGroupIcon(group.id, file)
      .catch(ex => setErr(ex.message || 'Failed to update picture'))
      .finally(() => setIconBusy(false));
  };

  const save = () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    setErr('');
    chat.renameGroup(group.id, name.trim(), description.trim())
      .then(() => { notify('Saved', { title: 'Updated', variant: 'success' }); onClose(); })
      .catch(e => setErr(e.message || 'Failed to save'))
      .finally(() => setBusy(false));
  };

  const remove = async () => {
    const label = group.kind === 'channel' ? 'channel' : 'group';
    if (!(await confirmDialog(`Delete this ${label}? This removes it and all its messages for everyone — this can't be undone.`))) return;
    setBusy(true);
    chat.deleteGroup(group.id)
      .then(() => { notify(`${label[0].toUpperCase() + label.slice(1)} deleted`, { title: 'Deleted', variant: 'success' }); onClose(); })
      .catch(e => { setErr(e.message || 'Failed to delete'); setBusy(false); });
  };

  return (
    <div className="absolute right-3 top-[52px] z-20 w-[260px] bg-bg1 border border-b0 rounded-[10px] shadow-[0_8px_24px_rgba(15,23,42,0.18)] p-3">
      <div className="text-[12.5px] font-bold text-t0 mb-2">{group?.kind === 'channel' ? 'Channel' : 'Group'} settings</div>
      <div className="flex items-center gap-2 mb-2">
        <button type="button" onClick={() => iconInputRef.current?.click()} disabled={iconBusy} className="w-[44px] h-[44px] rounded-full overflow-hidden border border-b0 bg-bg2 flex items-center justify-center flex-shrink-0 cursor-pointer disabled:opacity-60" title="Change picture">
          {group?.avatar_path ? <img src={uploadUrl(group.avatar_path)} alt="" className="w-full h-full object-cover" /> : <Camera size={16} className="text-t3" />}
        </button>
        <input ref={iconInputRef} type="file" accept="image/*" onChange={pickIcon} className="hidden" />
        <div className="text-[11px] text-t3 leading-[1.3]">{iconBusy ? 'Uploading…' : 'Click the picture to change it'}</div>
      </div>
      <input value={name} onChange={e => setName(e.target.value)} placeholder="Name (emoji allowed)" className="w-full mb-2 py-[7px] px-[9px] bg-bg2 border border-b0 rounded-[7px] text-t0 text-[12px] outline-none" />
      {group?.kind === 'channel' && (
        <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Description (optional)" rows={2} className="w-full mb-2 py-[7px] px-[9px] bg-bg2 border border-b0 rounded-[7px] text-t0 text-[12px] outline-none resize-none" />
      )}
      {err && <div className="text-[11px] text-r mb-2">{err}</div>}
      <div className="flex gap-[6px] mb-2">
        <button onClick={onClose} className="flex-1 py-[7px] rounded-[7px] bg-bg2 text-t0 text-[11.5px] font-semibold border-0 cursor-pointer">Cancel</button>
        <button onClick={save} disabled={!name.trim() || busy} className="flex-1 py-[7px] rounded-[7px] bg-p text-white text-[11.5px] font-semibold border-0 cursor-pointer disabled:opacity-50">
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
      <button onClick={remove} disabled={busy} className="w-full flex items-center justify-center gap-[6px] py-[7px] rounded-[7px] bg-rb text-r text-[11.5px] font-semibold border-0 cursor-pointer disabled:opacity-50">
        <Trash2 size={13} /> Delete {group?.kind === 'channel' ? 'channel' : 'group'}
      </button>
    </div>
  );
};

const InviteGuestPanel = ({ chat, groupId, onClose, isGroupOwner = false }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [status, setStatus] = useState(null); // null | 'sending' | {url} | {error}
  const [orgQuery, setOrgQuery] = useState('');
  const [addingId, setAddingId] = useState(null);
  const [orgErr, setOrgErr] = useState('');
  // Org people who aren't in this group yet — search narrows the list live.
  const memberIds = new Set((chat.groupMembers || []).map(m => m.user_id));
  const q = orgQuery.trim().toLowerCase();
  const orgMatches = (chat.people || [])
    .filter(p => p.user_id && !memberIds.has(p.user_id))
    .filter(p => !q || [p.name, p.email, p.google_email, p.designation].some(v => (v || '').toLowerCase().includes(q)))
    .slice(0, 30);
  const addPerson = (p) => {
    setAddingId(p.user_id); setOrgErr('');
    chat.addMembers(groupId, [p.user_id])
      .catch(err => setOrgErr(err.message || 'Could not add them'))
      .finally(() => setAddingId(null));
  };

  const send = () => {
    if (!email.trim()) return;
    setStatus('sending');
    chat.inviteGuest(groupId, email.trim(), name.trim())
      .then(res => setStatus({ url: res?.url }))
      .catch(err => setStatus({ error: err.message || 'Failed to send invite' }));
  };

  return (
    <div className="absolute right-3 top-[52px] z-20 w-[260px] bg-bg1 border border-b0 rounded-[10px] shadow-[0_8px_24px_rgba(15,23,42,0.18)] p-3">
      <div className="text-[12.5px] font-bold text-t0 mb-2">Add people</div>
      {isGroupOwner && (
        <div className="mb-3">
          <input value={orgQuery} onChange={e => setOrgQuery(e.target.value)} placeholder="Search people in the organization…" autoFocus className="w-full mb-2 py-[7px] px-[9px] bg-bg2 border border-b0 rounded-[7px] text-t0 text-[12px] outline-none" />
          <div className="max-h-[150px] overflow-y-auto border border-b0 rounded-[7px]">
            {orgMatches.length === 0 && <div className="text-[11px] text-t3 px-[9px] py-[8px]">{q ? 'No one matches that search.' : 'Everyone in the organization is already here.'}</div>}
            {orgMatches.map(p => (
              <button key={p.user_id} onClick={() => addPerson(p)} disabled={addingId !== null} className="w-full flex items-center gap-[8px] px-[9px] py-[6px] bg-transparent border-0 cursor-pointer hover:bg-bg2 text-left disabled:opacity-50">
                <span className="text-[11.5px] font-semibold text-t0 truncate flex-1">{p.name}{p.designation ? <span className="font-normal text-t3"> · {p.designation}</span> : null}</span>
                <span className="text-[10px] font-bold flex-shrink-0" style={{ color: K.p }}>{addingId === p.user_id ? 'Adding…' : '+ Add'}</span>
              </button>
            ))}
          </div>
          {orgErr && <div className="text-[11px] text-r mt-1">{orgErr}</div>}
          <div className="text-[10.5px] font-bold text-t3 mt-3 mb-1">Or invite someone outside the organization</div>
        </div>
      )}
      {status?.url ? (
        <div className="text-[11.5px] text-t2 leading-[1.4]">
          Invite created. Share this link directly (it also went by email):
          <div className="mt-1 p-2 bg-bg2 rounded-[6px] break-all text-[10.5px]" style={{ color: K.p }}>{status.url}</div>
          <button onClick={onClose} className="mt-2 w-full py-[6px] rounded-[7px] bg-bg2 text-t0 text-[11.5px] font-semibold border-0 cursor-pointer">Done</button>
        </div>
      ) : (
        <>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Their name (optional)" className="w-full mb-2 py-[7px] px-[9px] bg-bg2 border border-b0 rounded-[7px] text-t0 text-[12px] outline-none" />
          <input value={email} onChange={e => setEmail(e.target.value)} placeholder="their.email@company.com" className="w-full mb-2 py-[7px] px-[9px] bg-bg2 border border-b0 rounded-[7px] text-t0 text-[12px] outline-none" />
          {status?.error && <div className="text-[11px] text-r mb-2">{status.error}</div>}
          <div className="flex gap-[6px]">
            <button onClick={onClose} className="flex-1 py-[7px] rounded-[7px] bg-bg2 text-t0 text-[11.5px] font-semibold border-0 cursor-pointer">Cancel</button>
            <button onClick={send} disabled={!email.trim() || status === 'sending'} className="flex-1 py-[7px] rounded-[7px] bg-p text-white text-[11.5px] font-semibold border-0 cursor-pointer disabled:opacity-50">
              {status === 'sending' ? 'Sending…' : 'Send invite'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const GroupThreadView = ({ chat, myUserId, group, onForward, onOpenThread, isAdmin }) => {
  const [text, setText] = useState("");
  const [showMembers, setShowMembers] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const s = useInChatSearch(chat.groupMessages);
  const [isRecording, setIsRecording] = useState(false);
  const isGroupOwner = chat.groupMembers.find(m => m.user_id === myUserId)?.role === 'owner';
  const promoteMember = (userId) => chat.promoteMember(chat.activeGroupId, userId)
    .catch(e => notify(e.message || 'Failed to make admin', { title: 'Error', variant: 'error' }));
  const demoteMember = (userId) => chat.demoteMember(chat.activeGroupId, userId)
    .catch(e => notify(e.message || 'Failed to remove admin rights', { title: 'Error', variant: 'error' }));
  const removeMember = (userId) => chat.removeMember(chat.activeGroupId, userId)
    .then(() => notify('Removed from the group', { title: 'Removed', variant: 'success' }))
    .catch(e => notify(e.message || 'Failed to remove', { title: 'Error', variant: 'error' }));
  const [recordSecs, setRecordSecs] = useState(0);
  const [replyingTo, setReplyingTo] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const typingTimeoutRef = useRef(null);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const staged = useStagedFiles();
  const recorderRef = useRef(null);
  const recordCancelRef = useRef(false);
  const recordChunksRef = useRef([]);
  const recordStreamRef = useRef(null);
  const recordTimerRef = useRef(null);

  const typingNames = chat.groupTypingUsers
    .map(uid => chat.groupMembers.find(m => m.user_id === uid)?.name?.split(' ')[0])
    .filter(Boolean);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [chat.groupMessages, typingNames.length]);

  useEffect(() => {
    setReplyingTo(null);
    staged.clear();
    return () => {
      clearInterval(recordTimerRef.current);
      recordStreamRef.current?.getTracks().forEach(t => t.stop());
    };
  }, [chat.activeGroupId]);

  const handleChange = (val) => {
    setText(val);
    chat.setGroupTyping(chat.activeGroupId, true);
    clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => chat.setGroupTyping(chat.activeGroupId, false), 1500);
  };

  const mentionMatch = text.match(/(?:^|\s)@([a-zA-Z]*)$/);
  const mentionQuery = mentionMatch ? mentionMatch[1].toLowerCase() : null;
  const mentionCandidates = mentionQuery !== null
    ? chat.groupMembers.filter(m => m.user_id !== myUserId && m.name.toLowerCase().includes(mentionQuery)).slice(0, 5)
    : [];

  const selectMention = (member) => {
    setText(t => { const idx = t.lastIndexOf('@'); return t.slice(0, idx) + '@' + member.name + ' '; });
  };

  // Self-correcting: re-derives mentions from the final text right before
  // sending rather than tracking them through edits — if the "@Name" text
  // gets deleted, it just naturally won't match here, no stale mention left.
  const computeMentions = (finalText) => chat.groupMembers.filter(m => finalText.includes('@' + m.name)).map(m => m.user_id);

  // "Reply" (inline quote, stays in this group) is distinct from "Reply in
  // thread" (onOpenThread, a separate Threads-tab conversation).
  const startReply = (m) => setReplyingTo({
    id: m.id, body: m.body, attachment_kind: m.attachment_kind, attachment_name: m.attachment_name,
    sender_name: m.sender_id === myUserId ? 'You' : m.sender_name,
  });

  const scrollToMessage = (id) => {
    const el = s.msgRefs.current[id];
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightId(id);
    setTimeout(() => setHighlightId(null), 1500);
  };

  const send = () => {
    if (staged.files.length) {
      const caption = text.trim();
      staged.files.forEach((f, i) => chat.sendGroupAttachment(chat.activeGroupId, f.file, attachmentKindFor(f.file), i === 0 ? caption : '', i === 0 ? computeMentions(caption) : [], i === 0 ? replyingTo : null));
      staged.clear();
      setText("");
      setReplyingTo(null);
      clearTimeout(typingTimeoutRef.current);
      chat.setGroupTyping(chat.activeGroupId, false);
      return;
    }
    if (!text.trim()) return;
    chat.sendGroupMessage(chat.activeGroupId, text, computeMentions(text), replyingTo);
    setText("");
    setReplyingTo(null);
    clearTimeout(typingTimeoutRef.current);
    chat.setGroupTyping(chat.activeGroupId, false);
  };

  const onPickFile = (e) => {
    const picked = e.target.files;
    staged.add(picked);
    e.target.value = '';
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recordStreamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recordChunksRef.current = [];
      recordCancelRef.current = false;
      recorder.ondataavailable = (e) => { if (e.data.size > 0) recordChunksRef.current.push(e.data); };
      recorder.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        if (recordCancelRef.current) return; // Cancel: discard, never send
        const blob = new Blob(recordChunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        if (blob.size > 0) {
          const file = new File([blob], 'voice-message.webm', { type: blob.type });
          chat.sendGroupAttachment(chat.activeGroupId, file, 'audio', '', [], replyingTo);
          setReplyingTo(null);
        }
      };
      recorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
      setRecordSecs(0);
      recordTimerRef.current = setInterval(() => setRecordSecs(s => s + 1), 1000);
    } catch (err) {
      console.error('Mic access denied or unavailable:', err);
    }
  };

  const stopRecording = (cancel = false) => {
    clearInterval(recordTimerRef.current);
    setIsRecording(false);
    recordCancelRef.current = cancel;
    if (cancel) recordChunksRef.current = [];
    recorderRef.current?.stop();
  };

  const fmtSecs = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  return (
    <>
      <div className="relative flex items-center gap-2 px-3 py-[10px] border-b border-b0 flex-shrink-0">
        {group?.avatar_path ? (
          <img src={uploadUrl(group.avatar_path)} alt="" className="w-[32px] h-[32px] rounded-full object-cover flex-shrink-0" />
        ) : (
          <div className="w-[32px] h-[32px] rounded-full flex items-center justify-center flex-shrink-0 text-white" style={{ background: group?.kind === 'channel' ? K.tl : K.pu }}>
            {group?.kind === 'channel' ? <Hash size={15} /> : <Users size={15} />}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-bold text-t0 truncate">{group?.name || '...'}</div>
          <div className="text-[10.5px] text-t3 truncate">{group?.member_count || chat.groupMembers.length} member{(group?.member_count || chat.groupMembers.length) === 1 ? '' : 's'}</div>
        </div>
        <button
          onClick={() => { s.setSearchOpen(!s.searchOpen); setShowInvite(false); setShowMembers(false); setShowSettings(false); }}
          className={`w-[30px] h-[30px] rounded-[8px] bg-transparent flex items-center justify-center border-0 cursor-pointer hover:bg-bg3 flex-shrink-0 ${s.searchOpen ? 'text-p' : 'text-t3'}`}
          title="Search in this conversation"
        >
          <Search size={15} />
        </button>
        <button onClick={() => chat.startCall({ type: 'group', groupId: chat.activeGroupId })} className="w-[30px] h-[30px] rounded-[8px] bg-transparent text-t3 flex items-center justify-center border-0 cursor-pointer hover:bg-bg3 flex-shrink-0" title="Start a video call">
          <Video size={15} />
        </button>
        <ScheduleMeetingPanel
          sendFn={(t) => chat.sendGroupMessage(chat.activeGroupId, t)}
          isAdmin={isAdmin}
          attendeeEmails={chat.groupMembers.filter(m => m.user_id !== myUserId && m.google_email).map(m => m.google_email)}
          people={chat.people}
          groupMembers={chat.groupMembers.filter(m => m.user_id !== myUserId)}
        />
        <button onClick={() => { setShowInvite(v => !v); setShowMembers(false); setShowSettings(false); }} className="w-[30px] h-[30px] rounded-[8px] bg-transparent text-t3 flex items-center justify-center border-0 cursor-pointer hover:bg-bg3 flex-shrink-0" title="Add people">
          <UserPlus size={15} />
        </button>
        <button onClick={() => { setShowMembers(v => !v); setShowInvite(false); setShowSettings(false); }} className="w-[30px] h-[30px] rounded-[8px] bg-transparent text-t3 flex items-center justify-center border-0 cursor-pointer hover:bg-bg3 flex-shrink-0" title="Members">
          <Users size={15} />
        </button>
        {isGroupOwner && (
          <button onClick={() => { setShowSettings(v => !v); setShowMembers(false); setShowInvite(false); }} className="w-[30px] h-[30px] rounded-[8px] bg-transparent text-t3 flex items-center justify-center border-0 cursor-pointer hover:bg-bg3 flex-shrink-0" title={`${group?.kind === 'channel' ? 'Channel' : 'Group'} settings`}>
            <Settings size={15} />
          </button>
        )}
        {showMembers && <MemberList members={chat.groupMembers} myUserId={myUserId} isGroupOwner={isGroupOwner} onPromote={promoteMember} onDemote={demoteMember} onRemove={removeMember} />}
        {showInvite && <InviteGuestPanel chat={chat} groupId={chat.activeGroupId} onClose={() => setShowInvite(false)} isGroupOwner={isGroupOwner} />}
        {showSettings && <GroupSettingsPanel chat={chat} group={group} onClose={() => setShowSettings(false)} />}
      </div>
      {s.searchOpen && <SearchBar s={s} />}
      <PinnedMessagesBar chat={chat} />
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-3 py-3 flex flex-col gap-[2px]"
        style={{ backgroundColor: K.pg, backgroundImage: `radial-gradient(${K.p}22 1px, transparent 1px)`, backgroundSize: '18px 18px' }}
      >
        {chat.groupMessages.length === 0 && typingNames.length === 0 && (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6 gap-2">
            <Users size={28} className="text-t3 opacity-60" />
            <div className="text-[13px] text-t3">No messages yet. Say hi to the group!</div>
          </div>
        )}
        {chat.groupMessages.map((m, idx) => {
          const mine = m.sender_id === myUserId;
          const hasAttachment = !!m.attachment_kind;
          const iAmMentioned = (m.mentioned_user_ids || []).includes(myUserId);
          // Tight spacing within a run of consecutive messages from the same
          // sender, more breathing room between runs — otherwise several
          // blue "mine" bubbles in a row just read as one solid blue block.
          const isNewRun = idx === 0 || chat.groupMessages[idx - 1].sender_id !== m.sender_id;
          // "Mine" text bubbles use the softer bg-pl, not the vivid bg-p,
          // for the same reason.
          const bubbleClass = hasAttachment
            ? `bg-bg1 border ${iAmMentioned ? 'border-a' : 'border-b0'} text-t0 ${m.attachment_kind === 'image' ? 'p-[3px]' : 'p-2'} ${mine ? 'rounded-br-[4px]' : 'rounded-bl-[4px]'}`
            : `px-3 py-[7px] ${mine ? 'bg-pl text-white rounded-br-[4px]' : (iAmMentioned ? 'bg-ab border border-a text-t0' : 'bg-bg3 text-t0') + ' rounded-bl-[4px]'}`;
          const dayPill = (idx === 0 || dayKeyOf(chat.groupMessages[idx - 1].created_at) !== dayKeyOf(m.created_at)) ? <DaySeparator ts={m.created_at} /> : null;
          if (m.system_event) return <React.Fragment key={m.id || idx}>{dayPill}<SystemLine m={m} /></React.Fragment>;
          if (m.deleted_at) return <React.Fragment key={m.id || idx}>{dayPill}<DeletedLine m={m} mine={mine} isNewRun={isNewRun} /></React.Fragment>;
          return (
            <React.Fragment key={m.id || idx}>{dayPill}
            <div
              key={m.id || idx}
              ref={el => { s.msgRefs.current[m.id] = el; }}
              className={`group flex items-end ${mine ? 'justify-end' : 'justify-start'} gap-[6px] ${isNewRun ? 'mt-[8px]' : ''} ${(s.isActiveMatch(m.id) || highlightId === m.id) ? 'bg-ab/60 -mx-2 px-2 rounded-xl' : ''}`}
            >
              {!mine && <ClickableAv userId={m.sender_id} nm={m.sender_name} sz={26} src={m.sender_photo} />}
              {mine && <MessageActionsMenu m={m} onForward={onForward} onOpenThread={onOpenThread} onReact={(id, emoji) => chat.toggleReaction(id, emoji)} onTogglePin={(id, pinned) => chat.togglePinMessage(id, pinned)} onDelete={mine ? (id) => chat.deleteMessage(id) : undefined} onEdit={mine ? (id, body) => chat.editMessage(id, body) : undefined} onReply={startReply} className="mb-[4px]" />}
              <div className={`flex flex-col max-w-[70%] ${mine ? 'items-end' : 'items-start'}`}>
                <div className={`rounded-2xl text-[13px] leading-[1.4] ${bubbleClass}`}>
                  {!mine && <div className="text-[11px] font-bold px-3 pt-[6px]" style={{ color: K.p }}>{m.sender_name}</div>}
                  <QuotedMessageBlock m={m} mine={mine} onClick={scrollToMessage} />
                  {hasAttachment && (
                    <div className={`${m._uploading ? 'opacity-60' : ''} ${!mine ? 'px-3 pb-[2px]' : ''}`}>
                      <Attachment m={m} />
                    </div>
                  )}
                  {m.body && (
                    <div className={`break-words whitespace-pre-wrap ${hasAttachment ? 'px-[6px] pt-[6px]' : (!mine ? 'px-3' : '')}`}>
                      {renderMessageBody(m.body, { mentionedUserIds: m.mentioned_user_ids, members: chat.groupMembers, mine })}
                    </div>
                  )}
                  <div className={`flex items-center gap-1 justify-end text-[10px] ${hasAttachment ? 'text-t3 px-[6px] pt-[3px] pb-[2px]' : (mine ? 'text-white/75 mt-[3px] pr-3 pb-[6px]' : 'text-t3 mt-[3px] px-3 pb-[6px]')}`}>
                    {m._uploading && <span>Uploading…</span>}
                    {m._failed && <span>Failed to send</span>}
                    {mine && !m._uploading && !m._failed && !m.system_event && <GroupTick m={m} light={hasAttachment} />}
                    {m.edited_at && <span>Edited</span>}<span>{msgTime(m.created_at)}</span>
                  </div>
                </div>
                <ReactionBar m={m} onReact={chat.toggleReaction} mine={mine} />
              </div>
              {!mine && <MessageActionsMenu m={m} onForward={onForward} onOpenThread={onOpenThread} onReact={(id, emoji) => chat.toggleReaction(id, emoji)} onTogglePin={(id, pinned) => chat.togglePinMessage(id, pinned)} onDelete={mine ? (id) => chat.deleteMessage(id) : undefined} onEdit={mine ? (id, body) => chat.editMessage(id, body) : undefined} onReply={startReply} className="mb-[4px]" />}
            </div>
          </React.Fragment>
          );
        })}
        {typingNames.length > 0 && (
          <div className="flex items-center gap-2">
            <TypingDots />
            <span className="text-[11px] text-t3">{typingNames.join(', ')} typing…</span>
          </div>
        )}
      </div>
      <ReplyPreviewBar replyingTo={replyingTo} onCancel={() => setReplyingTo(null)} />
      <StagedFilesBar staged={staged} onAdd={() => fileInputRef.current?.click()} />
      <div className={`flex items-center gap-2 px-3 relative ${staged.files.length ? 'pt-[6px] pb-[10px]' : 'py-[10px] border-t border-b0'}`}>
        <input ref={fileInputRef} type="file" multiple accept={ATTACHMENT_ACCEPT} onChange={onPickFile} className="hidden" />
        {mentionCandidates.length > 0 && (
          <div className="absolute left-3 right-3 bottom-[52px] z-20 bg-bg1 border border-b0 rounded-[10px] shadow-[0_8px_24px_rgba(15,23,42,0.18)] py-1 max-h-[160px] overflow-y-auto">
            {mentionCandidates.map(m => (
              <button key={m.user_id} onClick={() => selectMention(m)} className="w-full flex items-center gap-[8px] px-3 py-[6px] bg-transparent border-0 cursor-pointer hover:bg-bg3 text-left">
                <Av nm={m.name} sz={22} rn={K.p} src={m.photo} />
                <span className="text-[12px] font-semibold text-t0">{m.name}</span>
              </button>
            ))}
          </div>
        )}
        {isRecording ? (
          <div className="flex-1 flex items-center gap-2 py-[8px] px-[12px] bg-bg2 border border-b0 rounded-[10px]">
            <span className="w-[8px] h-[8px] rounded-full bg-r animate-pulse flex-shrink-0" />
            <span className="text-[13px] text-t0 flex-1">Recording… {fmtSecs(recordSecs)}</span>
            <button onClick={() => stopRecording(true)} className="bg-transparent border-0 text-t3 cursor-pointer p-0 text-[12px] font-semibold">Cancel</button>
          </div>
        ) : (
          <>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-[34px] h-[34px] rounded-[10px] bg-transparent text-t2 flex items-center justify-center border-0 cursor-pointer hover:bg-bg3 flex-shrink-0"
              title="Send a file or image"
            >
              <Paperclip size={16} />
            </button>
            <input
              value={text}
              onChange={e => handleChange(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Message the group... use @ to mention"
              data-esc-clears
              className="flex-1 py-[8px] px-[12px] bg-bg2 border border-b0 rounded-[10px] text-t0 text-[13px] outline-none"
            />
          </>
        )}
        {!isRecording && !text.trim() && !staged.files.length ? (
          <button
            onClick={startRecording}
            className="w-[34px] h-[34px] rounded-[10px] bg-p text-white flex items-center justify-center border-0 cursor-pointer flex-shrink-0"
            title="Record a voice message"
          >
            <Mic size={15} />
          </button>
        ) : (
          <button
            onClick={isRecording ? () => stopRecording(false) : send}
            disabled={!isRecording && !text.trim() && !staged.files.length}
            className="w-[34px] h-[34px] rounded-[10px] bg-p text-white flex items-center justify-center border-0 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
          >
            {isRecording ? <Square size={14} /> : <Send size={15} />}
          </button>
        )}
      </div>
    </>
  );
};

// ── Create Group / Create Channel / Browse Channels overlays ──────────────
const NewGroupModal = ({ chat, kind, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState('organization'); // 'organization' | 'personal' — channel only
  const [iconFile, setIconFile] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);
  const [selected, setSelected] = useState([]);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const iconInputRef = useRef(null);
  const isChannel = kind === 'channel';
  const filtered = chat.people.filter(p => (p.name || '').toLowerCase().includes(q.trim().toLowerCase()));

  const toggle = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const pickIcon = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setIconFile(file);
    setIconPreview(URL.createObjectURL(file));
  };

  const create = () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    const promise = isChannel
      ? chat.createChannel(name.trim(), description.trim(), selected, visibility)
      : chat.createGroup(name.trim(), selected);
    promise
      .then(g => (iconFile ? chat.setGroupIcon(g.id, iconFile).then(() => g) : g))
      .then(g => { onCreated(g.id); onClose(); })
      .catch(() => setBusy(false));
  };

  return (
    <div className="fixed inset-0 z-[600] bg-black/40 flex items-center justify-center p-4" {...useBackdropDismiss(onClose)}>
      <div className="w-full max-w-[420px] max-h-[85vh] bg-bg1 border border-b0 rounded-2xl shadow-[0_18px_48px_rgba(15,23,42,0.3)] flex flex-col overflow-hidden animate-modalIn" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-[12px] border-b border-b0">
          <span className="text-[14px] font-extrabold text-t0">{isChannel ? 'New Channel' : 'New Group'}</span>
          <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer p-1 rounded-lg hover:bg-bg3"><X size={16} /></button>
        </div>
        <div className="p-4 flex-1 overflow-y-auto flex flex-col gap-[10px]">
          {isChannel && (
            <div className="flex items-center gap-[8px] mb-[2px]">
              <input ref={iconInputRef} type="file" accept="image/*" onChange={pickIcon} className="hidden" />
              <button
                onClick={() => iconInputRef.current?.click()}
                className="w-[52px] h-[52px] rounded-full flex items-center justify-center flex-shrink-0 border-0 cursor-pointer overflow-hidden bg-bg2 text-t3"
                title="Channel image"
              >
                {iconPreview ? <img src={iconPreview} alt="" className="w-full h-full object-cover" /> : <Camera size={18} />}
              </button>
              <div className="text-[11px] text-t3 leading-[1.4]">Optional channel image — click to upload.</div>
            </div>
          )}
          <input value={name} onChange={e => setName(e.target.value)} placeholder={isChannel ? 'Channel name' : 'Group name'} className="w-full py-[9px] px-[12px] bg-bg2 border border-b0 rounded-[10px] text-t0 text-[13px] outline-none" />
          {isChannel && (
            <>
              <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="What's this channel for? (optional)" rows={2} className="w-full py-[9px] px-[12px] bg-bg2 border border-b0 rounded-[10px] text-t0 text-[13px] outline-none resize-none" />
              <div className="flex gap-[8px]">
                {[
                  { key: 'organization', icon: Building2, label: 'Organization', hint: 'Anyone can find and join' },
                  { key: 'personal', icon: Lock, label: 'Personal', hint: 'Invite-only' },
                ].map(({ key, icon: Icon, label, hint }) => (
                  <button
                    key={key}
                    onClick={() => setVisibility(key)}
                    className={`flex-1 flex flex-col items-center gap-[4px] py-[10px] rounded-[10px] border cursor-pointer transition-colors ${visibility === key ? 'bg-pg border-p' : 'bg-bg2 border-b0 hover:bg-bg3'}`}
                  >
                    <Icon size={16} className={visibility === key ? 'text-p' : 'text-t3'} />
                    <span className={`text-[11.5px] font-bold ${visibility === key ? 'text-p' : 'text-t0'}`}>{label}</span>
                    <span className="text-[9.5px] text-t3">{hint}</span>
                  </button>
                ))}
              </div>
            </>
          )}
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Add participants..." className="w-full py-[8px] px-[12px] bg-bg2 border border-b0 rounded-[10px] text-t0 text-[12.5px] outline-none" />
          {/* Fixed max-height, not flex-1 — nested inside another scrollable
              flex-1 container, a flex-1 here collapses toward zero instead
              of actually taking 180px, since the ancestor's own main-axis
              size is itself content-driven (classic nested flex+overflow
              pitfall). */}
          <div className="flex-shrink-0 overflow-y-auto border border-b0 rounded-[10px] max-h-[240px] min-h-[120px]">
            {filtered.map(p => (
              <button key={p.user_id} onClick={() => toggle(p.user_id)} className="w-full flex items-center gap-[8px] px-3 py-[8px] bg-transparent border-0 cursor-pointer hover:bg-bg2 text-left">
                <div className={`w-[16px] h-[16px] rounded-[4px] border flex items-center justify-center flex-shrink-0 ${selected.includes(p.user_id) ? 'bg-p border-p' : 'border-b1'}`}>
                  {selected.includes(p.user_id) && <Check size={11} color="#fff" />}
                </div>
                <Av nm={p.name} sz={26} rn={K.p} src={p.photo} />
                <span className="text-[12.5px] font-semibold text-t0 truncate">{p.name}</span>
              </button>
            ))}
          </div>
          <div className="text-[11px] text-t3">{selected.length} selected</div>
        </div>
        <div className="flex gap-[8px] p-4 border-t border-b0">
          <button onClick={onClose} className="flex-1 py-[9px] rounded-[10px] bg-bg2 text-t0 text-[12.5px] font-semibold border-0 cursor-pointer">Cancel</button>
          <button onClick={create} disabled={!name.trim() || busy} className="flex-1 py-[9px] rounded-[10px] bg-p text-white text-[12.5px] font-semibold border-0 cursor-pointer disabled:opacity-50">
            {busy ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
};

const BrowseChannelsModal = ({ chat, onClose, onJoined }) => {
  // Load once on open only — `chat` is a fresh object every render, so
  // depending on it would refetch on every keystroke/incoming-message
  // re-render instead.
  useEffect(() => { chat.loadChannels(); }, []);
  const [joiningId, setJoiningId] = useState(null);

  const join = (id) => {
    setJoiningId(id);
    chat.joinChannel(id).then(() => { onJoined(id); onClose(); }).catch(() => setJoiningId(null));
  };

  return (
    <div className="fixed inset-0 z-[600] bg-black/40 flex items-center justify-center p-4" {...useBackdropDismiss(onClose)}>
      <div className="w-full max-w-[380px] max-h-[85vh] bg-bg1 border border-b0 rounded-2xl shadow-[0_18px_48px_rgba(15,23,42,0.3)] flex flex-col overflow-hidden animate-modalIn" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-[12px] border-b border-b0">
          <span className="text-[14px] font-extrabold text-t0">Browse Channels</span>
          <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer p-1 rounded-lg hover:bg-bg3"><X size={16} /></button>
        </div>
        <div className="p-2 flex-1 overflow-y-auto">
          {chat.channels.length === 0 && <div className="text-[13px] text-t3 text-center py-8">No channels to join right now.</div>}
          {chat.channels.map(c => (
            <div key={c.id} className="flex items-center gap-[10px] px-3 py-[9px] rounded-xl">
              {c.avatar_path ? (
                <img src={uploadUrl(c.avatar_path)} alt="" className="w-[36px] h-[36px] rounded-full object-cover flex-shrink-0" />
              ) : (
                <div className="w-[36px] h-[36px] rounded-full flex items-center justify-center flex-shrink-0 text-white" style={{ background: K.tl }}>
                  <Hash size={16} />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-[13.5px] font-bold text-t0 truncate">{c.name}</div>
                <div className="text-[11px] text-t3 truncate">{c.description || `${c.member_count} members`}</div>
              </div>
              <button onClick={() => join(c.id)} disabled={joiningId === c.id} className="px-3 py-[6px] rounded-[8px] bg-p text-white text-[11.5px] font-bold border-0 cursor-pointer disabled:opacity-50 flex-shrink-0">
                {joiningId === c.id ? 'Joining…' : 'Join'}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Two-pane layout: the people/recent list (left) stays visible at all
// times, and the selected conversation opens beside it (right) — rather
// than the list being replaced by the thread, which hid who else you could
// message as soon as you opened one.
// Nudge shown at the top of the panel for someone who hasn't connected
// their own Google Calendar yet — fetched fresh on every open (ChatPanel
// only mounts while the widget is open) and dismissible per-session, so it
// quietly reappears the next time they open the chat rather than being
// gone forever after one dismissal, until they actually connect.
const CalendarConnectNudge = ({ onConnect, onDismiss, connecting }) => (
  <div className="mx-3 mt-[10px] flex-shrink-0 flex items-center gap-[10px] px-3 py-[9px] rounded-[10px] border border-p/40 bg-pg">
    <div className="w-[30px] h-[30px] rounded-full bg-p/15 flex items-center justify-center flex-shrink-0 animate-ringPulse">
      <Calendar size={15} className="text-p" />
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-[12px] font-bold text-t0">Connect your Google Calendar</div>
      <div className="text-[10.5px] text-t3 leading-[1.3]">Simply connect it to auto-join the Meet link on every call, and to book scheduled meetings directly on your calendar.</div>
    </div>
    <button onClick={onConnect} disabled={connecting} className="px-3 py-[6px] rounded-[7px] bg-p text-white text-[11px] font-bold border-0 cursor-pointer disabled:opacity-60 flex-shrink-0">
      {connecting ? 'Opening…' : 'Connect'}
    </button>
    <button onClick={onDismiss} className="w-[22px] h-[22px] rounded-full bg-transparent border-0 text-t3 flex items-center justify-center cursor-pointer hover:bg-bg3 flex-shrink-0" title="Dismiss for now">
      <X size={13} />
    </button>
  </div>
);

export const ChatPanel = ({ chat, myUserId, isAdmin, onClose }) => {
  const [tab, setTab] = useState('chats'); // 'chats' | 'threads'
  const [maximized, setMaximized] = useState(false);
  const [modal, setModal] = useState(null); // null | 'group' | 'channel' | 'browse'
  const [forwardMessage, setForwardMessage] = useState(null);
  const [profilePerson, setProfilePerson] = useState(null);

  // Esc clears whichever conversation is open (so the panel goes blank for
  // whoever is looking over your shoulder), unless focus is in a text field
  // or a modal is up — those keep Esc for their own dismiss behaviour.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (profilePerson || modal || forwardMessage) return;
      const tag = (document.activeElement?.tagName || '').toLowerCase();
      const inField = tag === 'input' || tag === 'textarea' || document.activeElement?.isContentEditable;
      if (inField && !document.activeElement?.hasAttribute('data-esc-clears')) return;
      if (chat.activeGroupId) chat.closeGroup();
      else if (chat.activeUserId) chat.closeThread();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [chat, profilePerson, modal, forwardMessage]);
  const [calStatus, setCalStatus] = useState(null);
  const [calDismissed, setCalDismissed] = useState(false);
  const [calConnecting, setCalConnecting] = useState(false);

  useEffect(() => {
    api.get('/api/google-calendar/status').then(d => { if (d) setCalStatus(d); }).catch(() => {});
  }, []);

  const connectCalendar = () => {
    setCalConnecting(true);
    api.get('/auth/google/connect')
      .then(d => { if (d?.url) window.open(d.url, '_blank', 'noopener,noreferrer'); })
      .finally(() => setCalConnecting(false));
  };
  const activePerson = chat.activeUserId
    ? (chat.people.find(p => p.user_id === chat.activeUserId)
      || chat.conversations.find(c => c.user_id === chat.activeUserId))
    : null;
  const activeGroup = chat.activeGroupId ? chat.groups.find(g => g.id === chat.activeGroupId) : null;
  const threadsUnreadCount = chat.threads.filter(t => !t.last_read_at || new Date(t.last_reply_at) > new Date(t.last_read_at)).length;
  const openThreadFor = (m) => { chat.openThreadView(m.id); setTab('threads'); };

  return (
    <ProfileCtx.Provider value={{ open: setProfilePerson, people: chat.people }}>
    <div
      className={`animate-dropIn bg-bg1 border border-b0 shadow-[0_18px_48px_rgba(15,23,42,0.22)] overflow-hidden flex flex-col ${
        // Full edge-to-edge when maximized (no margin, no rounded corners —
        // a real "maximize", not a slightly-bigger floating card), and a
        // z-index above EVERYTHING else in the app shell (the top Header
        // bar alone runs z-[300]) so it isn't rendered underneath it.
        // `relative` must NOT be combined with `fixed` here — Tailwind's
        // generated CSS orders the `.relative` rule after `.fixed`, so
        // having both classes present at once let `relative` silently win
        // and `fixed`/`inset-0` never actually applied, collapsing the
        // panel to its shrink-to-fit content size instead of expanding.
        maximized ? 'fixed inset-0 z-[500] rounded-none' : 'relative w-[640px] max-w-[94vw] h-[560px] max-h-[82vh] rounded-2xl'
      }`}
    >
      <div className="flex items-center justify-between px-3 py-[10px] border-b border-b0 bg-pg flex-shrink-0">
        <span className="text-[14px] font-extrabold text-t0">Messages</span>
        <div className="flex items-center gap-1">
          <button onClick={() => setMaximized(m => !m)} className="bg-transparent border-0 text-t3 cursor-pointer p-1 rounded-lg hover:bg-bg3" title={maximized ? 'Restore' : 'Maximize'}>
            {maximized ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          </button>
          <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer p-1 rounded-lg hover:bg-bg3">
            <X size={16} />
          </button>
        </div>
      </div>
      <div className="flex px-3 pt-[10px] gap-[8px] flex-shrink-0">
        {[['chats', 'Chats', chat.unreadTotal], ['threads', 'Threads', threadsUnreadCount]].map(([key, label, count]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`relative px-4 py-[6px] rounded-[8px] text-[12px] font-bold border-0 cursor-pointer transition-colors ${tab === key ? 'bg-p text-white' : 'bg-bg2 text-t3 hover:bg-bg3'}`}
          >
            {label}
            {count > 0 && (
              <span className="absolute -top-[6px] -right-[6px] min-w-[17px] h-[17px] px-[4px] rounded-full bg-r text-white text-[10px] font-bold flex items-center justify-center border-2 border-bg1">
                {count > 99 ? '99+' : count}
              </span>
            )}
          </button>
        ))}
      </div>
      {!calDismissed && calStatus?.configured && !calStatus?.connected && (
        <CalendarConnectNudge onConnect={connectCalendar} onDismiss={() => setCalDismissed(true)} connecting={calConnecting} />
      )}
      {tab === 'threads' ? (
        // chat.threads only lists roots that already have a reply (see
        // threads.js GET /mine), so right after "Reply in thread" on a
        // message with zero replies so far, the list is still empty even
        // though activeThreadRootId is set — gating on the list alone hid
        // the detail pane (and its reply box) before the first reply
        // existed to populate it. Gate on activeThreadRootId too.
        chat.threads.length === 0 && !chat.activeThreadRootId ? (
          <NoThreadsYet />
        ) : (
          <div className="flex-1 min-h-0 flex mt-[10px]">
            <div className={`flex-shrink-0 border-r border-b0 flex flex-col ${maximized ? 'w-[320px]' : 'w-[230px]'}`}>
              <ThreadsListView chat={chat} />
            </div>
            <div className="flex-1 min-w-0 flex flex-col">
              {chat.activeThreadRootId ? <ThreadDetailView chat={chat} myUserId={myUserId} /> : <NoThreadOpen />}
            </div>
          </div>
        )
      ) : (
        <div className="flex-1 min-h-0 flex mt-[10px]">
          <div className={`flex-shrink-0 border-r border-b0 flex flex-col ${maximized ? 'w-[320px]' : 'w-[230px]'}`}>
            <ListView chat={chat} myUserId={myUserId} onOpen={chat.openThread} onOpenGroup={chat.openGroup} onNewAction={setModal} onProfile={setProfilePerson} />
          </div>
          <div className="flex-1 min-w-0 flex flex-col">
            {chat.activeGroupId ? (
              <GroupThreadView chat={chat} myUserId={myUserId} group={activeGroup} onForward={setForwardMessage} onOpenThread={openThreadFor} isAdmin={isAdmin} />
            ) : chat.activeUserId ? (
              <ThreadView chat={chat} myUserId={myUserId} activePerson={activePerson} onForward={setForwardMessage} onOpenThread={openThreadFor} isAdmin={isAdmin} onProfile={() => setProfilePerson(activePerson)} />
            ) : (
              <NoThreadSelected />
            )}
          </div>
        </div>
      )}
      {profilePerson && <PersonProfileModal p={profilePerson} onClose={() => setProfilePerson(null)} onMessage={profilePerson.user_id !== myUserId ? (uid) => { setProfilePerson(null); setTab('chats'); chat.openThread(uid); } : undefined} />}
      {(modal === 'group' || modal === 'channel') && (
        <NewGroupModal chat={chat} kind={modal} onClose={() => setModal(null)} onCreated={chat.openGroup} />
      )}
      {modal === 'browse' && (
        <BrowseChannelsModal chat={chat} onClose={() => setModal(null)} onJoined={chat.openGroup} />
      )}
      {forwardMessage && (
        <ForwardPickerModal chat={chat} message={forwardMessage} onClose={() => setForwardMessage(null)} />
      )}
    </div>
    </ProfileCtx.Provider>
  );
};
